import React from "react";
import { Icon, LogoMark, CompanyLogo, PoweredBy, poweredByShown, Portal, PublicEstimatorForm, FloatingWidget, SitePlan, SignaturePad, hubQuoteView, GRADE_LABELS, quotePayments, quoteDeposit, financingLive, fenceSpecFrom, Fence3DLauncher, quoteTiers, recommendedTierId } from "./registry";
import { ConcreteScopeTable } from "./registry";   // a concrete quote's scope on the hub, in place of the fence runs
import { verticalOf, brandOf } from "./lib/verticals";   // the service's brand on the hub (plan §5.7)
import { hubPlans, CARE_LABEL, everyLabel } from "./lib/careplans";   // the customer's own care plan: what and when (§13 item 15)
import { concretePrepFor } from "./lib/concrete.steps";   // a concrete job: what to do before the crew arrives, what to expect after (plan §5.4)
import { PublicConcreteForm } from "./concreteform";   // /widget/<token>?form=concrete — the photo estimate wizard (plan §5.5)
import { ConcreteSitePlan } from "./concretemap";   // the slabs drawn on the satellite view, on the customer's document (plan §5.1)
import { PlanSheet } from "./fenceplan";
import { downscaleToDataUrl } from "./lib/img";
import { customerTimeline, timelineWorkOrder, stepLabel, stepWhen, STEP_ICON, type HubStep } from "./lib/hubtimeline";
import { demoHubAction, demoHubThread, DemoWatermark, DemoHubBar } from "./demo";
import { shownCoverNote, shownValidUntil, expiryOf, validityLine, fmtDate, businessTz, signedOf, firstNameOf } from "./lib/quotedetails";
import { visualGroups } from "./lib/visuals";   // the quote's pictures by kind: the fence in their photo, the fence in 3D, the floor previewed (§13 item 27)
/* ============================================================
   Fence Works — Public Customer Hub (token-secured portal)
   Rendered for #/hub/<token> — no auth, looked up by token only.
   ============================================================ */

/* FW-021 — the Terms & Conditions are the SERVER's (server/src/contract.js).
   The hub payload carries state.terms = { version, text }, the sign card
   renders that, and the signature records the server's own copy — so what the
   customer read and what the contract says are the same object rather than two
   copies that drift apart the first time someone edits one of them.

   These constants are the fallback for a cached bundle that loads against an
   older payload. They must stay byte-identical to TERMS["fw-terms-v1"] on the
   server; contract.test.mjs fails if they don't. Changing what a contract says
   means adding a version there, never editing a published one. */

/* Who is this link for? The service resolves the token to ONE customer and
   returns a scoped state whose customer record is a contact card — the
   hub token is deliberately not in it (L01 allowlist). Trust the service's
   answer (`me`) first; matching the token only works against LOCAL state
   (the owner's own preview, the sandbox), where the record still carries it.
   Never guess from a local fallback: a stranger's dead link must not show
   the owner's one customer. */
/* The quote's pictures, one card per kind (lib/visuals.ts; §13 items 14 and
   27): the fence standing in the customer's photo, the fence built in 3D, the
   flake floor previewed — each the latest with a before/after slider when the
   before is there, the rest as thumbnails. Pure display. */
function HubVisuals({ visuals, thing }: { visuals: any[]; thing: string }) {
  const groups = visualGroups(visuals, thing);
  if (!groups.length) return null;
  return <>{groups.map((g) => <HubVisualGroup key={g.kind} group={g} />)}</>;
}
function HubVisualGroup({ group }: { group: { kind: string; title: string; note: string; list: any[] } }) {
  const [pos, setPos] = React.useState<number>(60);
  const [sel, setSel] = React.useState<number>(0);
  const list = group.list;
  const v = list[Math.min(sel, list.length - 1)];
  if (!v) return null;
  return (
    <div className="hub-card" data-hub-visuals={group.kind}>
      <div className="kicker" style={{ marginBottom: 10 }}>{group.title}</div>
      {v.before
        ? <div className="cp-stage" style={{ borderRadius: 10, overflow: "hidden" }}>
            <img src={v.before} alt="Your floor today" className="cp-before" style={{ position: "relative", display: "block", width: "100%" }} />
            <img src={v.url} alt={v.label || "Preview"} className="cp-after" style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }} />
            {pos > 0 && pos < 100 && <div className="cp-divider" style={{ left: pos + "%" }} />}
          </div>
        : <img src={v.url} alt={v.label || "Preview"} style={{ display: "block", width: "100%", borderRadius: 10 }} />}
      {v.before && <div className="row gap-8" style={{ alignItems: "center", marginTop: 8 }}><span className="muted" style={{ fontSize: 12 }}>Today</span><input type="range" min={0} max={100} value={pos} onChange={(e) => setPos(Number(e.target.value))} style={{ flex: 1 }} aria-label="Slide between today and the preview" /><span className="muted" style={{ fontSize: 12 }}>After</span></div>}
      <p className="muted" style={{ fontSize: 12.5, margin: "8px 0 0" }}>{v.label || "Preview"} — {group.note}</p>
      {list.length > 1 && <div className="row gap-6" style={{ flexWrap: "wrap", marginTop: 8 }}>{list.map((x: any, i: number) => <button key={x.id || i} onClick={() => setSel(i)} className={"cp-thumb" + (i === sel ? " on" : "")} style={{ backgroundImage: `url(${x.url})` }} title={x.label || "Preview"} aria-label={x.label || "Preview"} />)}</div>}
    </div>
  );
}

function resolveHubCustomer(state: any, token: any) {
  const customers = (state && state.customers) || [];
  if (!token) return null;
  const byToken = customers.find((c: any) => c && c.hubToken && c.hubToken === token);
  if (byToken) return byToken;
  const me = state && state.me;
  if (me) return customers.find((c: any) => c && c.id === me) || null;
  // an older service build: scoped (one customer, tokens stripped) but no `me` yet
  if (customers.length === 1 && state && state.scoped && !customers[0].hubToken) return customers[0];
  return null;
}

/* The company's Google rating, for the badge under the welcome. The service
   sends it (public.js hubRating) only when it's earned — 3+ Google reviews
   averaging 4.0+, hidden ones counted, private feedback never — and the same
   bar is held here, so a stale or odd payload can't show less. The sandbox,
   which has no service, shows its sample workspace's figure. The link is only
   the one the service vouched for (https). null = no badge. */
function hubRatingOf(state: any, sandbox: boolean) {
  const r = (state && state.rating) || (sandbox && state && state.settings && state.settings.demo && state.settings.demo.googleRating) || null;
  if (!r || !(Number(r.count) >= 3) || !(Number(r.average) >= 4)) return null;
  const url = typeof r.url === "string" && /^https:\/\//i.test(r.url) ? r.url : null;
  return { average: Math.round(Number(r.average) * 10) / 10, count: Math.round(Number(r.count)), url };
}
function HubRating({ rating }: any) {
  if (!rating) return null;
  const label = <><span style={{ color: "#f5b301", fontSize: 14 }} aria-hidden="true">★</span> <b className="tnum" style={{ color: "var(--fg)" }}>{rating.average.toFixed(1)}</b> <span className="muted">· {rating.count.toLocaleString()} Google review{rating.count === 1 ? "" : "s"}</span></>;
  const style: any = { display: "inline-flex", alignItems: "center", gap: 4, marginTop: 8, padding: "4px 11px", borderRadius: 999, border: "1px solid var(--line-2)", fontSize: 13, textDecoration: "none", color: "inherit" };
  return rating.url
    ? <a href={rating.url} target="_blank" rel="noopener nofollow" style={style} aria-label={rating.average.toFixed(1) + " stars from " + rating.count + " Google reviews"}>{label}</a>
    : <span style={style} aria-label={rating.average.toFixed(1) + " stars from " + rating.count + " Google reviews"}>{label}</span>;
}

const TERMS_VERSION = "fw-terms-v1";
const TERMS = [
  "Pricing is valid for 30 days from the estimate date.",
  "The selected deposit is due at signing; the balance is due on completion.",
  "Fence lengths are measured from the site plan and field-verified at install; final footage may adjust the total at the quoted per-foot rate.",
  "Changes to scope, materials, or layout require a written change order.",
  "Schedule may shift for weather, site access, or material availability.",
  "Workmanship is warranted for 1 year; materials carry the manufacturer's warranty.",
  "Cancellation before materials are ordered receives a full deposit refund.",
];
/* the concrete service's terms (docs/SERVICE_SWITCHER_PLAN.md §5.7): no fence
   footage clause; cure, access and weather; the warranty is each line's own */
const TERMS_CONCRETE_VERSION = "fw-terms-concrete-v2";
const TERMS_CONCRETE = [
  "Pricing is valid for 30 days from the estimate date.",
  "Any deposit shown on the estimate is due at signing; the balance is due on completion.",
  "Areas are measured on site; a change in the area, the lift required or the floor's condition is priced at the quoted rates and confirmed in writing before the work proceeds.",
  "Lifting priced by the pound of foam or by the cubic foot of void is estimated from the area and the lift required and billed on the material actually placed, as logged on site; the final amount is confirmed in writing before the balance is due.",
  "Changes to scope or materials require a written change order.",
  "Schedule may shift for weather, site access or material availability; lifting, sealing and coating need dry conditions above the product's minimum temperature.",
  "The customer keeps vehicles, furniture and foot traffic off the treated areas for the cure times stated on the estimate.",
  "Workmanship is warranted as stated on each line of the estimate; materials carry the manufacturer's warranty.",
  "Cancellation before materials are ordered receives a full deposit refund.",
];

/* the /build3d/<token> share page mounts the full viewer directly (no teaser).
   Like every other consumer, it loads fencebuild ONLY via React.lazy so
   three.js stays in its own chunk (see fencebuild.tsx header). */
const FenceBuild3DLazy: any = React.lazy(() => import("./fencebuild").then((m: any) => ({ default: m.FenceBuild3D })));

/* a customer's latest quote → the 3D viewer's props (spec / layout / quote /
   bom). Shared by the hub's "Watch your fence come together" card and the
   /build3d/<token> share page so both render the SAME fence. bom = the same
   engine pass the quote doc uses → invoice-matched step counts; on any engine
   hiccup the viewer falls back to its own geometry counts. */
function quote3DProps(latestQuote: any, state: any) {
  const products = [...((state && state.customProducts) || []), ...window.VFP.PRODUCTS];
  let bom = null;
  try {
    const v = hubQuoteView(latestQuote, state);
    const rows = v.quote.mat.filter(m => m.qty > 0).map(m => ({ item: m.label, qty: Math.round(m.qty) }));
    // gates + other add-on supplies ride on bomExtras now (per-size-tier gate rows)
    (Array.isArray(v.quote.bomExtras) ? v.quote.bomExtras : []).forEach((x: any) => {
      if (x && x.item && +x.qty > 0) rows.push({ item: x.item, qty: Math.round(+x.qty) });
    });
    if (rows.length) bom = rows;
  } catch (e) { /* viewer falls back to its own geometry counts */ }
  return {
    spec: fenceSpecFrom(products.find(p => p.id === latestQuote.productId), latestQuote.productName),
    layout: latestQuote.inputs && latestQuote.inputs.parcel,
    quote: latestQuote,
    bom,
  };
}

/* compact "Share 3D" on the hub's 3D card — the customer re-shares their own
   public /build3d/<token> viewer link (native share sheet where available,
   else copy to clipboard). */
function Share3DLink({ token }: any) {
  const [copied, setCopied] = React.useState<any>(false);
  if (!token) return null;
  const url = window.location.origin + "/build3d/" + encodeURIComponent(token);
  return (
    <button className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} onClick={() => {
      const nav: any = navigator;
      if (nav.share) { nav.share({ title: "My new fence in 3D", url }).catch(() => {}); return; }
      if (nav.clipboard) nav.clipboard.writeText(url).catch(() => {});
      setCopied(true); setTimeout(() => setCopied(false), 1800);
    }}><Icon name="send" size={12} />{copied ? "Link copied ✓" : "Share 3D"}</button>
  );
}

function PublicCustomerHub({ token: rawToken, state }: any) {
  const token = String(rawToken || "").split("?")[0];
  const paidReturn = /[?&]paid=1/.test(String(rawToken || ""));
  const [viewQuote, setViewQuote] = React.useState<any>(false);
  const [signedNow, setSignedNow] = React.useState<any>(false);
  const [signName, setSignName] = React.useState<any>("");
  const [agree, setAgree] = React.useState<any>(false);
  const [signErr, setSignErr] = React.useState<any>("");
  const [payPct, setPayPct] = React.useState<any>(50);
  const [payErr, setPayErr] = React.useState<any>("");
  const [lightbox, setLightbox] = React.useState<any>(null);
  const [showPlan, setShowPlan] = React.useState<any>(false);   // blueprint fence plan (inline)
  const [confirmNote, setConfirmNote] = React.useState<any>("");   // post-Stripe confirm couldn't land (webhook will)
  const [askState, setAskState] = React.useState<any>(null);       // expired quote → "Ask for an updated quote": null | sending | sent | error
  const customers = (state && state.customers) || [];
  const customer = resolveHubCustomer(state, token);
  const apiBase = (window.VFP_API_BASE || "").replace(/\/$/, "");
  // sandbox: on the visitor's own device the hub reads/writes the sandbox
  // store (no server); the same link opened on their phone is served from
  // the demo session on the server, which marks the state `sandbox`.
  const demoLocal = !!(window.VFP_DEMO && window.VFP_DEMO.active);
  const sandbox = demoLocal || !!(state && state.sandbox);
  const sandboxBranded = demoLocal ? window.VFP_DEMO.branded(state) : !!(state && state.sandbox && state.sandbox.branded);
  // Free plan's "Powered by Fence Works": the service decides (state.poweredBy)
  const poweredBy = poweredByShown(state && state.poweredBy, sandbox, state && state.settings && state.settings.plan);
  async function hubPost(path, body) {
    if (demoLocal) return demoHubAction(token, path, body);
    if (!apiBase) throw new Error("Online actions aren't available in preview mode.");
    const r = await fetch(`${apiBase}/payments/hub/${encodeURIComponent(token)}/${path}`, {
      method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    });
    const d = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(d.message || d.error || ("Request failed (" + r.status + ")"));
    return d;
  }
  async function startCheckout(q, pct, method?: any) {
    setPayErr("");
    try {
      const d = await hubPost("checkout", { quoteId: q.id, pct, ...(method ? { method } : {}) });
      if (d && d.url) window.location.href = d.url;
    } catch (e) { setPayErr(String((e && e.message) || e)); }
  }
  function payFinal(q, balance, method?: any) { startCheckout(q, "balance", method); }
  // returning from Stripe with the session id → confirm the payment server-side
  // (doesn't depend on the webhook), then reload to pull the now-paid state.
  React.useEffect(() => {
    if (!paidReturn || !apiBase) return;
    const m = String(rawToken || "").match(/[?&]session_id=([^&]+)/);
    const sid = m && decodeURIComponent(m[1]);
    if (!sid) return;
    const confirm = () => hubPost("confirm", { sessionId: sid })
      .then(() => {
        // drop session_id so the reload doesn't re-confirm; keep paid=1 for the banner
        try { window.history.replaceState({}, "", "/hub/" + token + "?paid=1"); } catch (e) {}
        window.location.reload();
      });
    // the confirm can miss (cold service, flaky network): retry once after 3s,
    // then say so — Stripe has the money and the webhook reconciles it anyway
    confirm().catch(() => setTimeout(() => confirm().catch(() => setConfirmNote("Payment received by Stripe — it can take a minute to show here.")), 3000));
  }, []);
  let company = (state && state.settings && state.settings.companyName) || "Fence Works";
  let companyPhone = String((state && state.company && state.company.phone) || (state && state.settings && state.settings.companyPhone) || "").trim();

  if (!customer) {
    return (
      <div className="hub-shell"><div className="hub-card" style={{ textAlign: "center", maxWidth: 440 }}>
        <LogoMark size={36} />
        <h2 className="display" style={{ fontSize: 20, margin: "14px 0 6px" }}>Link not found</h2>
        <p className="muted" style={{ fontSize: 13.5 }}>
          {token === "preview"
            ? <>This is a placeholder preview link — it isn't tied to a customer, so there's nothing to show. Open the quote and copy the customer's link again (it's customer-specific now).</>
            : <>This portal link is invalid or has been revoked. Please ask {company} for an updated link.</>}
        </p>
      </div></div>
    );
  }

  const quotes = (state.quotes || []).filter(q => q.customerId === customer.id || (q.customer || "").toLowerCase() === (customer.name || "").toLowerCase());
  const orders = (state.workOrders || []).filter(w => w.customerId === customer.id || quotes.some(q => q.id === w.quoteId));
  const jobPhotos = orders.flatMap((o: any) => ((o.photos || []) as any[]).map((p, i) => ({ url: p.url, name: p.name, at: p.at, tag: p.tag || null, by: p.by || null, key: o.id + "-" + i })));
  const latestQuote = quotes[quotes.length - 1];
  // which service this customer's project is (lib/verticals.ts): the fence
  // pieces — 3D build, site plan, blueprint, "your fence" — show for a fence
  const isFence = !latestQuote || verticalOf(latestQuote) === "fence";
  const thing = isFence ? "fence" : "concrete";
  // the service's brand (plan §5.7): the server already overlays it on the
  // payload's company/settings for the latest quote's service; the sandbox and
  // the owner's own device resolve it here the same way
  const brand = brandOf(state, isFence ? "fence" : "concrete");
  if (brand.name) company = brand.name;
  if (brand.phone) companyPhone = brand.phone;
  // LIVE total — the stored q.total is an estimate-time snapshot that goes
  // stale when pricing/settings change; the Quote & Invoice doc recomputes,
  // so the landing card and pay balance must match it or they contradict.
  const liveTotalOf = (qq: any) => {
    try { return hubQuoteView(qq, state).quote.customerTotal; }
    catch (e) { return Number(qq.total) || 0; }
  };
  const liveTotal = latestQuote ? liveTotalOf(latestQuote) : 0;
  /* the contractor's note and the valid-until date (lib/quotedetails.ts).
     The date is a day on the BUSINESS's calendar (settings.timezone), the
     same one the server signs against. Once it's signed neither decides
     anything, so an accepted quote never reads "expired". */
  const lqSigned = !!latestQuote && (signedNow || signedOf(latestQuote));
  const lqExpiry = latestQuote && !lqSigned && latestQuote.status !== "lost"
    ? expiryOf(shownValidUntil(latestQuote), { tz: businessTz(state && state.settings) }) : expiryOf(null);
  const coverNote = latestQuote ? shownCoverNote(latestQuote, { firstName: firstNameOf(latestQuote.customer || customer.name), company }) : "";
  // one tap from an expired quote to the contractor: through the same
  // message thread as "Message us" (it lands in their Messages)
  async function askForUpdate() {
    if (!latestQuote || askState === "sending") return;
    setAskState("sending");
    const body = `Could you send me an updated quote? The one for my ${latestQuote.productName || "fence"} expired on ${fmtDate(lqExpiry.validUntil, lqExpiry.today)}.`;
    try {
      if (demoLocal) await hubPost("message", { body });
      else {
        if (!apiBase) throw new Error("offline");
        const r = await fetch(`${apiBase}/public/hub/${encodeURIComponent(token)}/message`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ body }) });
        if (!r.ok) throw new Error("message " + r.status);
      }
      setAskState("sent");
    } catch (e) { setAskState("error"); }
  }
  // FW-021: the terms the server will record, so the card can't show one thing
  // and the contract say another. The constants are the fallback for a cached
  // bundle loading against a payload that predates this.
  const termsShown: { version: string; text: string[] } =
    (state && state.terms && Array.isArray(state.terms.text) && state.terms.text.length)
      ? state.terms : (isFence ? { version: TERMS_VERSION, text: TERMS } : { version: TERMS_CONCRETE_VERSION, text: TERMS_CONCRETE });
  // the job to track: prefer the work order tied to the quote being shown, then
  // any with a real schedule — a stray duplicate/blank order at the end of the
  // list was hiding the install details (dates, crew, ETA) from the customer.
  const wo = (latestQuote && orders.find(w => w.quoteId === latestQuote.id))
    || orders.slice().reverse().find(w => w.scheduledStart || w.status === "in_progress" || w.status === "completed")
    || orders[orders.length - 1];

  const STAGES = [
    { id: "quote", label: "Quote sent", icon: "send" },
    { id: "unscheduled", label: "Deposit paid", icon: "dollar" },
    { id: "scheduled", label: "Scheduled", icon: "calendar" },
    { id: "in_progress", label: "Install in progress", icon: "bolt" },
    { id: "completed", label: "Completed", icon: "check" },
  ];
  let stageIdx = 0;
  if (latestQuote && latestQuote.status === "paid") stageIdx = 1;
  if (wo) { const m = { unscheduled: 1, scheduled: 2, in_progress: 3, completed: 4 }; stageIdx = Math.max(stageIdx, m[wo.status] != null ? m[wo.status] : 1); }
  // the milestones: from the server on a real link (latestQuote.timeline);
  // from the app's own records when the page renders unscoped state (the
  // sandbox, the Settings preview). A scoped payload from an older server has
  // neither, and keeps the five stages above.
  const steps: HubStep[] | null = !latestQuote ? null
    : Array.isArray(latestQuote.timeline) ? latestQuote.timeline
    : !(state && state.scoped) ? customerTimeline(latestQuote, timelineWorkOrder(latestQuote, orders), { total: liveTotal })
    : null;
  const nextIdx = steps ? steps.findIndex((s: HubStep) => !s.done) : -1;

  const fmt = (t) => t ? new Date(t).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

  return (
    <div className={"hub-shell" + (sandbox && !sandboxBranded ? " demo-doc" : "")}>
      {sandbox && <DemoHubBar local={demoLocal && !/demo_frame=1/.test(String(rawToken || "") + location.search)} />}
      {sandbox && !sandboxBranded && <DemoWatermark fixed />}
      <div className="hub-topbar">
        <div className="row gap-10" style={{ alignItems: "center" }}><CompanyLogo company={brand.name ? brand : (state && state.company)} size={26} /><b style={{ fontSize: 16 }}>{company}</b></div>
        <span className="pubest-secure"><Icon name="lock" size={12} />Private link</span>
      </div>

      <div className="hub-main">
        <div className="hub-hero">
          <span className="kicker kicker-teal">Your {thing} project</span>
          <h1 className="display" style={{ fontSize: 26, margin: "6px 0 4px" }}>Welcome, {(customer.name || "there").split(" ")[0]}</h1>
          <p className="muted" style={{ fontSize: 14 }}>{customer.address || "Your property"}</p>
          <HubRating rating={hubRatingOf(state, sandbox)} />
        </div>

        {paidReturn && (
          <div className="hub-card" style={{ borderColor: "var(--green)", textAlign: "center" }}>
            <b style={{ color: "var(--green)" }}>🎉 Payment received — thank you!</b>
            <p className="muted" style={{ fontSize: 12.5, margin: "6px 0 0" }}>Your receipt is on its way from Stripe. {company} will reach out to schedule your install.</p>
            {confirmNote && <p className="faint" style={{ fontSize: 11.5, margin: "6px 0 0" }}>{confirmNote}</p>}
          </div>
        )}

        {/* progress timeline: the job's milestones with their dates (the
            server's allowlisted steps on a real link; built from the app's own
            records in the sandbox and the Settings preview), else the stages */}
        <div className="hub-card">
          <div className="kicker" style={{ marginBottom: 16 }}>Project status</div>
          {steps ? (
            <ol className="hub-steps" aria-label="Your project, step by step">
              {steps.map((s: HubStep, i: number) => {
                const next = !s.done && i === nextIdx;
                return (
                  <li key={s.key} className={"hub-step" + (s.done ? " done" : "") + (next ? " current" : "")} aria-current={next ? "step" : undefined}>
                    <div className="hub-stage-dot"><Icon name={s.done ? "check" : (STEP_ICON[s.key] || "clock")} size={13} /></div>
                    <div className="hub-step-label">{stepLabel(s)}{next && <span className="hub-step-next">Up next</span>}</div>
                    <div className="hub-step-when">{stepWhen(s)}</div>
                  </li>
                );
              })}
            </ol>
          ) : (
          <div className="hub-timeline">
            {STAGES.map((s, i) => (
              <div key={s.id} className={"hub-stage" + (i < stageIdx ? " done" : "") + (i === stageIdx ? " current" : "")}>
                <div className="hub-stage-dot"><Icon name={i < stageIdx ? "check" : s.icon} size={13} /></div>
                <div className="hub-stage-label">{s.label}</div>
              </div>
            ))}
          </div>
          )}
          {/* live install details — date, crew, materials ETA — straight off the work order */}
          {wo && (() => {
            const fd = (s: any) => s ? new Date(s + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "";
            const cr = ((state.settings && state.settings.crews) || []).find((c: any) => c.id === wo.crewId);
            const booked = !!wo.scheduledStart && ["scheduled", "in_progress", "completed", "done"].includes(String(wo.status || ""));
            return (
              <div className="row gap-8" style={{ marginTop: 14, flexWrap: "wrap" }}>
                {/* only a job that IS scheduled shows install dates. Reading the
                    date alone meant a job pulled back to Needs Scheduling kept
                    telling the customer their install was booked. */}
                {booked && <span className="chip" style={{ fontSize: 11.5 }}><Icon name="calendar" size={11} />Install {fd(wo.scheduledStart)}{wo.scheduledEnd && wo.scheduledEnd !== wo.scheduledStart ? " – " + fd(wo.scheduledEnd) : ""}</span>}
                {cr && booked && wo.status !== "completed" && <span className="chip" style={{ fontSize: 11.5 }}><Icon name="user" size={11} />{cr.name}{cr.foreman ? " · " + cr.foreman : ""}</span>}
                {(wo.status === "materials" || wo.status === "unscheduled") && wo.materialEta && <span className="chip" style={{ fontSize: 11.5 }}><Icon name="box" size={11} />Materials arriving ~{fd(wo.materialEta)}</span>}
                {wo.status === "completed" && <span className="chip green" style={{ fontSize: 11.5 }}><Icon name="check" size={11} />{isFence ? "Complete — enjoy the new fence!" : "Complete — thanks for having us out!"}</span>}
              </div>
            );
          })()}
        </div>

        {/* a change order waiting on the customer comes first — it changes every number below it */}
        {latestQuote && <HubCOApproval q={latestQuote} current={liveTotal} token={token} apiBase={apiBase} hubPost={hubPost} company={company} />}

        <div className="hub-grid">
          {/* quote / invoice */}
          <div className="hub-card">
            <div className="kicker" style={{ marginBottom: 12 }}>Quote &amp; invoice</div>
            {latestQuote ? (
              <>
                {/* the contractor's own note, first — text only (it's typed by
                    them, filled in for this customer, and never HTML) */}
                {coverNote && (
                  <div className="hub-cover-note" data-hub-cover-note>
                    <p className="quote-cover-text">{coverNote}</p>
                    <div className="quote-cover-sign">— {company}</div>
                  </div>
                )}
                <div className="row between" style={{ alignItems: "flex-end", marginBottom: 10 }}>
                  <div><div style={{ fontWeight: 700 }}>{latestQuote.productName}</div><div className="muted" style={{ fontSize: 12.5 }}>{Math.round(latestQuote.lf)} ft</div></div>
                  <div className="display" style={{ fontSize: 24, color: "var(--teal)" }}>{window.VFP.money0(liveTotal)}</div>
                </div>
                {lqExpiry.state !== "none" && (
                  <div className={"hub-valid " + lqExpiry.state} data-hub-valid={lqExpiry.state}>
                    <Icon name={lqExpiry.expired ? "x" : "clock"} size={12} />{validityLine(lqExpiry)}
                  </div>
                )}
                {/* named discount badge — the deal they got, right up front */}
                {(() => {
                  try {
                    const v = hubQuoteView(latestQuote, state);
                    if (v.quote.discountAmt > 0) return (
                      <div className="chip green" style={{ fontSize: 11, marginBottom: 10, display: "inline-flex" }}>
                        <Icon name="check" size={11} />{v.quote.discountLabel} — you saved {window.VFP.money0(v.quote.discountAmt)}
                      </div>
                    );
                  } catch (e) {}
                  return null;
                })()}
                {/* monthly financing, when the contractor offers it: the same
                    amortized estimate the quote screen shows. Applying opens once
                    they've accepted (the pay card below). */}
                {(() => {
                  if (!financingLive(state.settings)) return null;
                  const fin = (state.settings && state.settings.financing) || (window.VFP.DEFAULT_SETTINGS || {}).financing || {};
                  const bal = Math.max(0, liveTotal - quotePayments(latestQuote).paid);
                  const mo = window.VFP.monthlyPayment(bal, fin.apr, fin.termMonths);
                  if (!(mo > 0) || bal <= 0.005) return null;
                  const signed = latestQuote.status === "signed" || latestQuote.status === "paid" || !!(latestQuote.signature && latestQuote.signature.at);
                  return (
                    <p className="muted" style={{ fontSize: 12.5, margin: "0 0 10px" }}>
                      Or about <b className="tnum" style={{ color: "var(--fg-2)" }}>{window.VFP.money0(mo)}/mo</b> with financing
                      <span className="faint"> · est. {fin.termMonths} mo at {fin.apr}% APR{fin.partner ? " · " + fin.partner : ""}{signed ? "" : " · apply once you accept"}</span>
                    </p>
                  );
                })()}
                <HubOptions q={latestQuote} state={state} token={token} apiBase={apiBase} />
                {(() => {
                  const paid = quotePayments(latestQuote).paid;
                  const balance = Math.max(0, liveTotal - paid);
                  const isPaid = latestQuote.status === "paid";
                  const fullPaid = isPaid && paid > 0 && balance <= 0.005;
                  return (
                    <>
                      {isPaid && (
                        <div className="hub-pay-lines">
                          <div className="row between"><span className="muted" style={{ fontSize: 12.5 }}>Paid to date</span><span className="tnum" style={{ color: "var(--green)" }}>{window.VFP.money0(paid)}</span></div>
                          <div className="row between"><span className="muted" style={{ fontSize: 12.5 }}>Amount due</span><span className="tnum" style={{ fontWeight: 800, color: balance > 0.005 ? "var(--red)" : "var(--green)" }}>{window.VFP.money0(balance)}</span></div>
                        </div>
                      )}
                      <span className={"chip" + (fullPaid ? " green" : "")} style={{ textTransform: "capitalize", marginTop: 4, display: "inline-flex" }}>{fullPaid ? "Paid in full ✓" : isPaid ? "Deposit paid ✓" : lqExpiry.expired ? "Expired" : latestQuote.status}</span>
                      <div className="row gap-8" style={{ marginTop: 12, flexWrap: "wrap" }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setViewQuote(true)}><Icon name="clipboard" size={13} />View quote &amp; invoice</button>
                      </div>
                      {isPaid && balance > 0 && (
                        <div style={{ marginTop: 10 }}>
                          <HubPayChoice settings={state.settings} amount={balance} label="Make final payment" onPay={(m: any) => payFinal(latestQuote, balance, m)} />
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            ) : <p className="muted" style={{ fontSize: 13 }}>Your quote will appear here once it's ready.</p>}
          </div>

          {/* a concrete job: what to do before the crew arrives and what to expect
              after — composed from the job types, with the cure times frozen on
              the customer document (lib/concrete.steps.ts concretePrepFor) */}
          {!isFence && latestQuote && (() => {
            const doc = (latestQuote.customerDoc && latestQuote.customerDoc.quote) || {};
            const cure: any = {};
            ((((doc.concrete || {}).blocks) || []) as any[]).forEach((b) => { if (b && b.jobType && b.time && !cure[b.jobType]) cure[b.jobType] = b.time; });
            const prep = concretePrepFor(latestQuote.jobTypes || [], cure);
            if (!prep.before.length && !prep.after.length) return null;
            const done = !!(wo && wo.status === "completed");
            return (
              <div className="hub-card" data-hub-prep>
                {!done && <>
                  <div className="kicker" style={{ marginBottom: 10 }}>Before we arrive</div>
                  <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6, fontSize: 13.5 }}>{prep.before.map((t: string, i: number) => <li key={i}>{t}</li>)}</ul>
                </>}
                <div className="kicker" style={{ margin: done ? "0 0 10px" : "14px 0 8px" }}>{done ? "After the work" : "What to expect after"}</div>
                <ul style={{ margin: 0, paddingLeft: 18, display: "grid", gap: 6, fontSize: 13.5 }}>{prep.after.map((t: string, i: number) => <li key={i}>{t}</li>)}</ul>
              </div>
            );
          })()}

          {/* a concrete lift, done: what the crew measured — how low each section sat and where it ended up (the lift log, shaped by hubscope.js) */}
          {!isFence && wo && Array.isArray((wo as any).liftLog) && (wo as any).liftLog.length > 0 && (
            <div className="hub-card" data-hub-lift>
              <div className="kicker" style={{ marginBottom: 10 }}>Before and after</div>
              <div style={{ display: "grid", gap: 6, fontSize: 13.5 }}>
                {(wo as any).liftLog.map((e: any, i: number) => <div key={i} className="row between" style={{ gap: 8 }}><span>{e.section}</span><span className="tnum muted">{Number(e.beforeIn) > 0 ? `${e.beforeIn}″ low` : "—"} → {Number(e.afterIn) > 0 ? `${e.afterIn}″` : "level"}</span></div>)}
              </div>
            </div>
          )}
          {/* the warranty the finished job carries, per job type, dated from the day it was done */}
          {!isFence && wo && Array.isArray((wo as any).warranty) && (wo as any).warranty.length > 0 && (
            <div className="hub-card" data-hub-warranty>
              <div className="kicker" style={{ marginBottom: 10 }}>Your warranty</div>
              <div style={{ display: "grid", gap: 8, fontSize: 13.5 }}>
                {(wo as any).warranty.map((t: any, i: number) => (
                  <div key={i}>
                    <div className="row gap-8" style={{ flexWrap: "wrap" }}><b>{({ lift: "Lifting & leveling", coat: "Floor coating", clean: "Cleaning", seal: "Sealing", caulk: "Caulking" } as any)[t.jobType] || t.jobType}</b>{t.years ? <span className="chip green" style={{ fontSize: 10.5 }}>{t.years} year{t.years === 1 ? "" : "s"}{t.until ? ` · through ${new Date(t.until + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}` : ""}</span> : null}</div>
                    {t.text && <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>{t.text}</div>}
                  </div>
                ))}
              </div>
              <p className="muted" style={{ fontSize: 12, margin: "10px 0 0" }}>Something settle or peel? Reply to any of our messages with a photo and we'll take a look.</p>
            </div>
          )}
          {/* the care plan (§13 item 15): what we come back for and when — the service hands over only jobType / nextDue / everyMonths */}
          {!isFence && customer && hubPlans((customer as any).carePlans).length > 0 && (
            <div className="hub-card" data-hub-careplan>
              <div className="kicker" style={{ marginBottom: 10 }}>Your care plan</div>
              <div style={{ display: "grid", gap: 8, fontSize: 13.5 }}>
                {hubPlans((customer as any).carePlans).map((p: any, i: number) => (
                  <div key={i} className="row between" style={{ gap: 8, flexWrap: "wrap" }}>
                    <b>{CARE_LABEL[p.jobType] || p.jobType}</b>
                    <span className="muted" style={{ fontSize: 12.5 }}>{everyLabel(p.everyMonths)} · next {new Date(p.nextDue + "T12:00:00").toLocaleDateString("en-US", { month: "short", year: "numeric" })}</span>
                  </div>
                ))}
              </div>
              <p className="muted" style={{ fontSize: 12, margin: "10px 0 0" }}>We'll reach out about a month before it's due. Want it sooner? Reply to any of our messages.</p>
            </div>
          )}
          {/* the flake floor drawn on their own garage photo (§13 item 14) — hosted pictures and a label, nothing else reaches here */}
          {latestQuote && Array.isArray((latestQuote as any).visuals) && (latestQuote as any).visuals.length > 0 && <HubVisuals visuals={(latestQuote as any).visuals} thing={thing} />}

          {/* schedule — confirmed date, or the customer's own pick from the
              contractor-offered, capacity-checked windows */}
          <div className="hub-card">
            <div className="kicker" style={{ marginBottom: 12 }}>Install schedule</div>
            {/* same rule as the status strip: booked means the JOB is scheduled,
                not that a date is sitting on the record from a booking that was
                pulled back. Otherwise the customer is told the crew is coming. */}
            {wo && wo.scheduledStart && ["scheduled", "in_progress", "completed", "done"].includes(String(wo.status || "")) ? (
              <div className="row gap-10" style={{ alignItems: "center" }}>
                <div className="hub-date"><Icon name="calendar" size={18} /></div>
                <div><div style={{ fontWeight: 700 }}>{fmt(wo.scheduledStart)}{wo.scheduledEnd && wo.scheduledEnd !== wo.scheduledStart ? " – " + fmt(wo.scheduledEnd) : ""}</div>
                  <div className="muted" style={{ fontSize: 12.5 }}>{wo.status === "completed" ? "Completed — thank you!" : "Crew scheduled"}</div></div>
              </div>
            ) : wo ? (
              <HubPickWindow wo={wo} token={token} apiBase={apiBase} company={company} phone={companyPhone} />
            ) : <p className="muted" style={{ fontSize: 13 }}>We'll post your install date here once it's booked.</p>}
          </div>
        </div>

        {/* watch your fence come together — 3D build sequence of the quoted product
            (quote3DProps = the same mapping the /build3d/<token> share page renders) */}
        {latestQuote && isFence && (
          <div className="hub-card">
            <div className="row between" style={{ marginBottom: 12, alignItems: "center" }}>
              <div className="kicker">Watch your fence come together</div>
              <Share3DLink token={token} />
            </div>
            <Fence3DLauncher
              {...quote3DProps(latestQuote, state)}
              sub="Your fence, assembled piece by piece — tap to explore it in 3D." />
          </div>
        )}

        {/* accept, e-sign & pay — the customer-side action (ESIGN-compliant) */}
        {latestQuote && (() => {
          const sig = latestQuote.signature;
          const isSigned = signedNow || !!(sig && sig.at) || latestQuote.status === "signed" || latestQuote.status === "paid";
          const total = liveTotal;
          const paid = quotePayments(latestQuote).paid;
          const fullyPaid = paid >= total - 0.005 && total > 0;
          if (fullyPaid) return null;
          // a bank transfer still clearing isn't collected yet, but it isn't owed
          // again either — offering it back as "due" is how a customer paid the
          // same balance twice (ACH, then card an hour later). Mirrors the
          // server's balanceOf (server/src/contract.js).
          const pendingList = Array.isArray(latestQuote.pendingPayments) ? latestQuote.pendingPayments : [];
          const pending = pendingList.reduce((s: number, p: any) => s + (Number(p && p.amount) || 0), 0);
          // a card payment carrying the card fee is authorized first and
          // captured a moment later (without the fee on debit/prepaid) — in
          // flight like ACH, but it's a card, not a bank transfer
          const cardPending = pendingList.filter((p: any) => p && p.source === "card").reduce((s: number, p: any) => s + (Number(p.amount) || 0), 0);
          const bankPending = Math.max(0, pending - cardPending);
          const remaining = Math.max(0, total - paid - pending);
          // per-contractor deposit policy (set in Customer Hub settings). Once a
          // deposit is in, the only option is the remaining balance — never more.
          const dep = (state.settings && state.settings.deposit) || {};
          const depMode = dep.mode === "full_only" || dep.mode === "custom" ? dep.mode : "options";
          const depOpts = depMode === "full_only" ? []
            : depMode === "custom" ? [Math.max(1, Math.min(99, Number(dep.customPct) || 50))]
            : (Array.isArray(dep.options) ? dep.options.map(Number) : [25, 50]).filter(n => n >= 1 && n <= 99).sort((a, b) => a - b);
          // the quote's OWN deposit — a per-quote override, or the service's
          // default stamped on it (a same-day lift: 0, paid on completion) —
          // wins over the policy, exactly as the quote page's terms box and the
          // server's offered amounts read it (quoteDeposit)
          const own = quoteDeposit(latestQuote, state.settings || {}, total);
          const ownPct = own.source === "quote" ? Math.round(own.pct) : null;
          const onCompletion = ownPct === 0;
          const payOpts = onCompletion ? [100] : ownPct != null && ownPct > 0 && ownPct < 100 ? [ownPct, 100] : [...(depOpts.length || depMode === "full_only" ? depOpts : [25, 50]), 100];
          const effPct = payOpts.includes(payPct) ? payPct : payOpts[0];
          const due = effPct === 100 ? total : total * (effPct / 100);
          const charge = Math.max(0, Math.min(due, remaining));
          return (
            <div className="hub-card">
              <div className="kicker" style={{ marginBottom: 12 }}>{isSigned ? (paid > 0 ? "Pay your balance" : (onCompletion ? "Pay when the job is done" : depMode === "full_only" ? "Pay for your project" : "Pay your deposit")) : lqExpiry.expired ? "Quote expired" : "Accept your quote"}</div>
              {isSigned && paid <= 0 && onCompletion && <p className="muted" style={{ fontSize: 13, margin: "-4px 0 12px" }}>Nothing is due up front — {company} collects the balance once the work is complete. You can pay it here any time.</p>}
              {!isSigned && lqExpiry.expired ? (
                /* past its valid-until date: no signature pad (the server
                   wouldn't take it) — one tap asks the contractor for a new one */
                <div className="hub-expired" data-hub-expired style={{ display: "grid", gap: 12 }}>
                  <p style={{ fontSize: 14, margin: 0 }}><b>This quote expired on {fmtDate(lqExpiry.validUntil, lqExpiry.today)}.</b> Ask {company} to refresh it — they'll send you an updated quote here.</p>
                  {askState === "sent" ? (
                    <p className="hub-ask-sent" style={{ margin: 0 }}><Icon name="check" size={14} /> Asked. {company} will send your updated quote here{companyPhone ? <> — or call <a href={"tel:" + companyPhone}>{companyPhone}</a></> : null}.</p>
                  ) : (
                    <button className="btn btn-primary btn-block" disabled={askState === "sending"} onClick={askForUpdate}>
                      <Icon name="send" size={14} />{askState === "sending" ? "Sending…" : "Ask for an updated quote"}
                    </button>
                  )}
                  {askState === "error" && <p style={{ color: "var(--amber)", fontSize: 12.5, margin: 0 }}>That didn't send — {companyPhone ? <>call {company} at <a href={"tel:" + companyPhone}>{companyPhone}</a></> : <>use "Message us" below</>}.</p>}
                </div>
              ) : !isSigned ? (
                <div style={{ display: "grid", gap: 12 }}>
                  <p className="muted" style={{ fontSize: 13, margin: 0 }}>Review the quote above, then sign below to accept it. {onCompletion ? "Nothing is due up front — you pay once the work is done." : "Your deposit locks in your spot on the schedule."}</p>
                  {lqExpiry.state !== "none" && (
                    <div className={"hub-valid " + lqExpiry.state} style={{ margin: 0 }}><Icon name="clock" size={12} />{validityLine(lqExpiry)}</div>
                  )}
                  <div>
                    <label className="field-label">Type your full legal name</label>
                    <input className="input" value={signName} onChange={e => setSignName(e.target.value)} placeholder="Your full name" autoComplete="name" />
                  </div>
                  <SignaturePad signed={false} onSign={async (image) => {
                    setSignErr("");
                    if (!agree) { setSignErr("Please review the Terms & Conditions and check the agreement box first."); return; }
                    if (!signName || signName.trim().length < 2) { setSignErr("Please type your full legal name first."); return; }
                    try {
                      // send back the version we actually RENDERED, so the server
                      // records the terms this customer read (it refuses a version
                      // it can't produce rather than recording a label it can't
                      // resolve to any text)
                      await hubPost("sign", { quoteId: latestQuote.id, name: signName.trim(), image, agree: true, termsVersion: termsShown.version });
                      setSignedNow(true);
                    } catch (e) { setSignErr(String((e && e.message) || e)); }
                  }} />
                  <details style={{ fontSize: 12, color: "var(--fg-3)", border: "1px solid var(--line)", borderRadius: 10, padding: "10px 12px" }}>
                    <summary style={{ cursor: "pointer", fontWeight: 700, color: "var(--fg-2)" }}>Terms &amp; Conditions ({termsShown.version})</summary>
                    <ol style={{ margin: "8px 0 0", paddingLeft: 18, display: "grid", gap: 4 }}>
                      {termsShown.text.map((x: string, i: number) => <li key={i}>{x}</li>)}
                    </ol>
                  </details>
                  <label className="row gap-8" style={{ fontSize: 12, color: "var(--fg-3)", alignItems: "flex-start", cursor: "pointer" }}>
                    <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ marginTop: 2 }} />
                    <span>I have reviewed this estimate, including its total of <b style={{ color: "var(--fg-2)" }}>{window.VFP.money(total)}</b>, and <b style={{ color: "var(--fg-2)" }}>I agree to the Terms &amp; Conditions above</b>. I consent to do business electronically, and I understand my signature below is a legally binding acceptance of this estimate (E-SIGN Act / UETA).</span>
                  </label>
                  {signErr && <p style={{ color: "var(--red)", fontSize: 12.5, margin: 0 }}>{signErr}</p>}
                </div>
              ) : (
                <div style={{ display: "grid", gap: 12 }}>
                  <div className="row gap-8" style={{ alignItems: "center" }}>
                    <Icon name="check" size={15} style={{ color: "var(--green)" }} />
                    <span style={{ fontSize: 13.5 }}>Quote accepted{(sig && sig.name) ? " — signed by " + sig.name : ""}{(sig && sig.at) ? " on " + new Date(sig.at).toLocaleDateString() : ""}</span>
                  </div>
                  {bankPending > 0 && (
                    <p style={{ fontSize: 12.5, margin: 0, color: "var(--fg-2)" }}>
                      <Icon name="clock" size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                      Bank transfer of {window.VFP.money0(bankPending)} on its way — it usually clears in 1–4 business days.
                    </p>
                  )}
                  {cardPending > 0 && (
                    <p style={{ fontSize: 12.5, margin: 0, color: "var(--fg-2)" }}>
                      <Icon name="clock" size={13} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                      Card payment of {window.VFP.money0(cardPending)} approved — we're finishing the charge. It shows as paid here once it goes through.
                    </p>
                  )}
                  {paid + pending > 0 ? (
                    <>
                      <p className="muted" style={{ fontSize: 12.5, margin: 0 }}>Paid so far: {window.VFP.money0(paid)}{pending > 0 ? ` · processing ${window.VFP.money0(pending)}` : ""} · remaining {window.VFP.money0(remaining)}</p>
                      {remaining >= 0.5 && <HubPayChoice settings={state.settings} amount={remaining} label="Pay your remaining balance" onPay={(m: any) => payFinal(latestQuote, remaining, m)} />}
                    </>
                  ) : (
                    <>
                      {payOpts.length > 1 && (
                        <div className="row gap-8" style={{ flexWrap: "wrap" }}>
                          {payOpts.map(p => (
                            <button key={p} className={"filter-chip" + (effPct === p ? " on" : "")} onClick={() => setPayPct(p)}>{p === 100 ? "Pay in full" : p + "% deposit"}</button>
                          ))}
                        </div>
                      )}
                      <HubPayChoice settings={state.settings} amount={charge} label={effPct === 100 ? "Pay in full" : "Pay deposit"} onPay={(m: any) => startCheckout(latestQuote, effPct, m)} />
                    </>
                  )}
                  {financingLive(state.settings) && (
                    <button className="btn btn-ghost btn-block" onClick={() => {
                      setPayErr("");
                      fetch(`${apiBase}/public/hub/${encodeURIComponent(token)}/financing`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ quoteId: latestQuote.id }) })
                        .then(r => r.json().then(d => ({ ok: r.ok, d })))
                        .then(({ ok, d }) => { if (ok && d.url) window.open(d.url, "_blank", "noopener"); else setPayErr(d.message || "Monthly financing isn't available right now."); })
                        .catch(() => setPayErr("Couldn't start financing."));
                    }}><Icon name="clipboard" size={14} />Or apply for monthly financing</button>
                  )}
                  {payErr && <p style={{ color: "var(--amber)", fontSize: 12.5, margin: 0 }}>{payErr}</p>}
                  <p className="faint" style={{ fontSize: 11, textAlign: "center", margin: 0 }}>{sandbox ? "Sandbox — no card is charged. In your real workspace this is Stripe, paid directly to you." : <>Secured by Stripe — paid directly to {company}. Cards &amp; ACH.</>}</p>
                  {/* FW-021: a signed contract the customer can no longer read
                      is a contract they can't hold anyone to. The terms stayed
                      behind the unsigned branch, so they vanished at the exact
                      moment they started binding someone. */}
                  <details style={{ fontSize: 12, color: "var(--fg-3)", border: "1px solid var(--line)", borderRadius: 10, padding: "10px 12px" }}>
                    <summary style={{ cursor: "pointer", fontWeight: 700, color: "var(--fg-2)" }}>What you agreed to ({(sig && sig.terms) || termsShown.version})</summary>
                    <ol style={{ margin: "8px 0 0", paddingLeft: 18, display: "grid", gap: 4 }}>
                      {termsShown.text.map((x: string, i: number) => <li key={i}>{x}</li>)}
                    </ol>
                    {sig && sig.consent && <p style={{ margin: "10px 0 0", fontStyle: "italic" }}>{sig.consent}</p>}
                  </details>
                </div>
              )}
            </div>
          );
        })()}

        {/* change orders awaiting THIS customer's e-sign — same signature flow
            as the quote, one audit-stamped approval per change */}

        {/* property & fence layout — the real site plan from the saved estimate geometry (fence projects) */}
        {isFence && <div className="hub-card">
          <div className="kicker" style={{ marginBottom: 12 }}>Your property &amp; fence layout</div>
          {latestQuote && latestQuote.inputs && (latestQuote.inputs.parcel || (latestQuote.inputs.mVerts || []).length > 1)
            ? <>
                <SitePlan inputs={latestQuote.inputs} />
                <p className="faint" style={{ fontSize: 11.5, textAlign: "center", margin: "8px 0 0" }}>Site plan from your estimate · not to scale — 3D fence preview coming soon</p>
                <div style={{ textAlign: "center", marginTop: 10 }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => setShowPlan((v: any) => !v)}><Icon name="map" size={13} />{showPlan ? "Hide blueprint" : "View the blueprint fence plan"}</button>
                </div>
                {showPlan && <HubFencePlan q={latestQuote} state={state} />}
              </>
            : <div className="hub-map"><Icon name="map" size={26} style={{ opacity: .5 }} /><span className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>Your fence layout will appear here once your estimate is drawn</span></div>}
        </div>}

        {/* job photos — grouped before → during → after when the crew tagged
            them (the transformation story); untagged shots keep the flat grid */}
        {jobPhotos.length > 0 && (
          <div className="hub-card">
            <div className="kicker" style={{ marginBottom: 12 }}>Project photos</div>
            {(() => {
              const grid = (ps: any[]) => (
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(96px, 1fr))", gap: 8 }}>
                  {ps.map((p: any) => (
                    <button key={p.key} onClick={() => setLightbox(p.url)} title={p.name || "Photo"}
                      style={{ aspectRatio: "1 / 1", border: 0, padding: 0, borderRadius: 10, cursor: "pointer",
                        backgroundImage: `url(${p.url})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                  ))}
                </div>
              );
              // the ones the customer sent (hub or text) sit in their own group, first
              const yours = jobPhotos.filter((p: any) => p.by === "customer"), theirs = jobPhotos.filter((p: any) => p.by !== "customer");
              const anyTag = theirs.some((p: any) => p.tag);
              if (!anyTag && !yours.length) return grid(jobPhotos);
              const groups = [
                ["yours", "From you"], ["before", "Before"], ["during", isFence ? "During the install" : "During the work"], ["after", isFence ? "After — your new fence" : "After"], [null, "More photos"],
              ].map(([tag, label]: any) => ({ label, ps: tag === "yours" ? yours : theirs.filter((p: any) => (p.tag || null) === tag) })).filter(g => g.ps.length);
              return (
                <div style={{ display: "grid", gap: 14 }}>
                  {groups.map((g: any) => (
                    <div key={g.label}>
                      <div className="muted" style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 6 }}>{g.label}</div>
                      {grid(g.ps)}
                    </div>
                  ))}
                </div>
              );
            })()}
            <p className="faint" style={{ fontSize: 11.5, margin: "10px 0 0" }}>Photos from your install — tap to enlarge.</p>
          </div>
        )}

        {/* the customer's own photos → the job, the open estimate or their card (§13 item 24) */}
        <HubSendPhotos token={token} isFence={isFence} wo={wo} latestQuote={latestQuote} sandboxLocal={demoLocal} apiBase={apiBase} />

        {/* two-way messaging */}
        <div className="hub-card">
          <div className="kicker" style={{ marginBottom: 12 }}>Questions? Message us</div>
          <HubMessenger company={company} thing={thing} token={token} initial={state.hubThread} sandboxLocal={demoLocal} post={hubPost} />
        </div>

        {/* the contractor's social profiles (Settings → Reputation & Social) */}
        {(() => {
          const sl = (state.settings && state.settings.socialLinks) || {};
          const links = [["Facebook", sl.facebook], ["Instagram", sl.instagram], ["Google", sl.google]].filter(([, u]) => u && String(u).trim());
          if (!links.length) return null;
          return (
            <div style={{ textAlign: "center", margin: "8px 0 0" }}>
              <span className="faint" style={{ fontSize: 11.5, marginRight: 10 }}>Follow {company}</span>
              {links.map(([label, url]: any) => (
                <a key={label} href={/^https?:\/\//i.test(url) ? url : "https://" + url} target="_blank" rel="noopener"
                  style={{ fontSize: 11.5, color: "var(--teal)", marginRight: 10, fontWeight: 600 }}>{label}</a>
              ))}
            </div>
          );
        })()}
        <p className="faint" style={{ fontSize: 11.5, textAlign: "center", margin: "8px 0 20px" }}>This is your private project link from {company}. Don't share it — anyone with the link can view your project.</p>
        {poweredBy && <PoweredBy medium="hub" />}
      </div>
      {lightbox && (
        <Portal>
        <div className="hub-preview-overlay" data-dialog onClick={() => setLightbox(null)}>
          <img src={lightbox} alt="" onClick={e => e.stopPropagation()}
            style={{ maxWidth: "92vw", maxHeight: "88vh", borderRadius: 12, boxShadow: "0 12px 48px rgba(0,0,0,.4)" }} />
        </div>
        </Portal>
      )}
      {viewQuote && latestQuote && (() => {
        let view = null;
        try { view = hubQuoteView(latestQuote, state); } catch (e) {}
        // the brand frozen on the signed document wins (what they agreed to), then the live brand
        const docBrand = (latestQuote.signedDoc && latestQuote.signedDoc.brand) || (latestQuote.customerDoc && latestQuote.customerDoc.brand) || null;
        const co: any = docBrand ? { ...brand, ...docBrand } : (brand.name ? brand : ((state && state.company) || {}));
        const coName = co.name || (state.settings && state.settings.companyName) || "Fence Works";
        const legalForm = (latestQuote.signedDoc && latestQuote.signedDoc.legalForm) || co.legalForm || "";
        const paid = quotePayments(latestQuote).paid;
        const total = view ? view.quote.customerTotal : (Number(latestQuote.total) || 0);
        const bal = Math.max(0, total - paid);
        const fullPaid = paid > 0 && bal <= 0.005;
        const sig = latestQuote.signature;
        return (
          <Portal>
          <div className="hub-preview-overlay" data-dialog onClick={() => setViewQuote(false)}>
            <div className="hub-quote-modal hub-doc-modal" onClick={e => e.stopPropagation()}>
              <div className="hub-preview-bar">
                <span className="row gap-8" style={{ fontSize: 13, fontWeight: 700 }}><Icon name="clipboard" size={14} className="muted" />Quote &amp; Invoice</span>
                <div className="row gap-8">
                  <button className="btn btn-ghost btn-sm" onClick={() => window.print()}><Icon name="printer" size={13} />Print</button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setViewQuote(false)}><Icon name="x" size={14} /></button>
                </div>
              </div>
              <div className="hub-quote-body">
                {/* the SAME document the contractor's Print view produces */}
                <div className="quote-doc">
                  <div className="quote-head">
                    <div className="row gap-12">
                      <CompanyLogo company={co} size={44} />
                      <div>
                        <div className="display" style={{ fontSize: 22 }}>{coName}</div>
                        <div className="muted mono" style={{ fontSize: 12 }}>{[co.address, co.phone].filter(Boolean).join(" · ") || " "}</div>
                      </div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div className="kicker">Estimate</div>
                      <div className="mono" style={{ fontSize: 13, marginTop: 4 }}>#{String(latestQuote.id).slice(-6).toUpperCase()}</div>
                      <div className="muted" style={{ fontSize: 12, marginTop: 2 }}>{latestQuote.createdAt ? new Date(latestQuote.createdAt).toLocaleDateString() : ""}</div>
                      {lqExpiry.state !== "none" && <div className={"q-valid-date" + (lqExpiry.expired ? " expired" : lqExpiry.soon ? " soon" : "")} style={{ fontSize: 12, marginTop: 2 }}>{lqExpiry.expired ? "Expired " : "Valid until "}{fmtDate(lqExpiry.validUntil, lqExpiry.today)}</div>}
                      <span className={"chip " + ({ pending: "", viewed: "", signed: "violet", paid: "green" } as any)[latestQuote.status] } style={{ marginTop: 8, textTransform: "capitalize" }}>{fullPaid ? "Paid in full" : lqExpiry.expired ? "Expired" : latestQuote.status}</span>
                    </div>
                  </div>

                  {coverNote && (
                    <div className="quote-cover-note" style={{ margin: "14px 0 0" }}>
                      <p className="quote-cover-text">{coverNote}</p>
                      <div className="quote-cover-sign">— {coName}</div>
                    </div>
                  )}

                  <div className="quote-for row between" style={{ margin: "18px 0 6px", alignItems: "flex-end" }}>
                    <div>
                      <div className="kicker">Prepared for</div>
                      <div style={{ fontWeight: 800, marginTop: 4 }}>{latestQuote.customer || customer.name}</div>
                      <div className="muted" style={{ fontSize: 12.5 }}>{latestQuote.address || customer.address || ""}</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div className="kicker">Project total</div>
                      <div className="display tnum" style={{ fontSize: 28, color: "var(--teal)", marginTop: 3 }}>{window.VFP.money(total)}</div>
                      {paid > 0 && !fullPaid && <div style={{ marginTop: 8 }}>
                        <div className="kicker" style={{ color: "var(--red)" }}>Amount due</div>
                        <div className="display tnum" style={{ fontSize: 28, color: "var(--red)", marginTop: 3 }}>{window.VFP.money(bal)}</div>
                        <div className="muted tnum" style={{ fontSize: 11.5, marginTop: 2 }}>{window.VFP.money0(paid)} of {window.VFP.money0(total)} paid</div>
                      </div>}
                      {fullPaid && <span className="chip green" style={{ marginTop: 8 }}><Icon name="check" size={11} />Paid in full</span>}
                    </div>
                  </div>

                  {view && (
                    <>
                      {view.quote && view.quote.concrete ? <><ConcreteScopeTable blocks={view.quote.concrete.blocks} kicker="What we're doing" kickerStyle={{ margin: "16px 0 6px" }} />{view.quote.concrete.plan && <ConcreteSitePlan plan={view.quote.concrete.plan} kicker="Where" kickerStyle={{ margin: "16px 0 6px" }} />}</> : <>
                      <div className="kicker" style={{ margin: "16px 0 6px" }}>Fence runs</div>
                      <table className="quote-table">
                        <thead><tr><th>Run</th><th>Fence type</th><th style={{ textAlign: "right" }}>Length</th><th style={{ textAlign: "right" }}>Grade</th></tr></thead>
                        <tbody>
                          {view.runGroups.map((g: any, gi: number) => (
                            <React.Fragment key={gi}>
                              {g.name && <tr className="run-group"><td colSpan={4}>{g.name} · {g.prod.name} · <span className="tnum">{Math.round(g.q.LF)} ft</span></td></tr>}
                              {g.rows.map((r: any, k: number) => (
                                <tr key={gi + "-" + k}>
                                  <td style={{ fontWeight: 600, paddingLeft: g.name ? 14 : 0 }}>{r.name}</td>
                                  <td className="muted" style={{ fontSize: 13 }}>{r.type}</td>
                                  <td className="tnum" style={{ textAlign: "right" }}>{Math.round(r.length)} ft</td>
                                  <td className="tnum muted" style={{ textAlign: "right", fontSize: 12.5 }}>{GRADE_LABELS[r.grade] || "Level"}</td>
                                </tr>
                              ))}
                            </React.Fragment>
                          ))}
                        </tbody>
                        <tfoot>
                          <tr><td colSpan={2} style={{ fontWeight: 700 }}>Total fence</td><td className="tnum" colSpan={2} style={{ textAlign: "right", fontWeight: 700, color: "var(--teal)" }}>{Math.round(view.quote.LF)} ft · {view.quote.panels} panels · {view.quote.posts} posts</td></tr>
                        </tfoot>
                      </table>
                      </>}

                      <div className="kicker" style={{ margin: "18px 0 6px" }}>Pricing</div>
                      <table className="quote-table">
                        <thead><tr><th>Description</th><th style={{ textAlign: "right" }}>Amount</th></tr></thead>
                        <tbody>
                          {view.lines.map((l: any, i: number) => (
                            <tr key={i}><td><div style={{ fontWeight: 600 }}>{l.label}</div>{l.sub && <div className="muted" style={{ fontSize: 12 }}>{l.sub}</div>}{typeof l.desc === "string" && l.desc && <div className="quote-line-desc">{l.desc}</div>}{typeof l.warranty === "string" && l.warranty && <div className="quote-line-desc"><b>Warranty:</b> {l.warranty}</div>}</td><td className="tnum" style={{ textAlign: "right" }}>{window.VFP.money(l.amount)}</td></tr>
                          ))}
                        </tbody>
                        <tfoot>
                          {(view.quote.tax > 0 || view.quote.discountAmt > 0) && <tr><td className="muted">Subtotal</td><td className="tnum" style={{ textAlign: "right" }}>{window.VFP.money(view.quote.subtotal)}</td></tr>}
                          {view.quote.discountAmt > 0 && <tr><td style={{ color: "var(--green)", fontWeight: 600 }}>{view.quote.discountLabel}</td><td className="tnum" style={{ textAlign: "right", color: "var(--green)", fontWeight: 600 }}>−{window.VFP.money(view.quote.discountAmt)}</td></tr>}
                          {view.quote.tax > 0 && <tr><td className="muted">{view.quote.taxLabel || "Sales tax"} ({String(+(view.quote.taxRate * 100).toFixed(4))}%)</td><td className="tnum" style={{ textAlign: "right" }}>{window.VFP.money(view.quote.tax)}</td></tr>}
                          {view.changeOrders && view.changeOrders.length > 0 && <>
                            <tr><td className="muted" style={{ fontWeight: 600 }}>Original contract (as signed)</td>
                              <td className="tnum" style={{ textAlign: "right", fontWeight: 600 }}>{window.VFP.money(view.signedTotal != null ? view.signedTotal
                                // unsigned: the total already carries the approved COs listed below
                                : total - view.changeOrders.filter((c: any) => c && (!c.status || c.status === "approved")).reduce((s: number, c: any) => s + (Number(c.amount) || 0), 0))}</td></tr>
                            {view.changeOrders.map((c: any) => (
                              <tr key={c.id}><td className="muted" style={{ fontSize: 11.5, paddingLeft: 12 }}>Change order{c.at ? " · " + new Date(c.at).toLocaleDateString() : ""} — {c.label}
                                {c.status === "pending" && <b style={{ color: "var(--amber)" }}> · awaiting your approval — not yet in the total</b>}
                                {c.photo && <a href={c.photo} target="_blank" rel="noopener" style={{ marginLeft: 6, color: "var(--teal)" }}>photo</a>}</td>
                                <td className="tnum muted" style={{ textAlign: "right", fontSize: 11.5, opacity: c.status === "pending" ? 0.55 : 1 }}>{(Number(c.amount) || 0) >= 0 ? "+" : "−"}{window.VFP.money(Math.abs(Number(c.amount) || 0))}</td></tr>
                            ))}
                          </>}
                          <tr><td style={{ fontWeight: 800 }}>{view.changeOrders && view.changeOrders.length ? "Revised total" : "Project total"}</td><td className="tnum display" style={{ textAlign: "right", fontSize: 18, color: "var(--teal)" }}>{window.VFP.money(total)}</td></tr>
                          {paid > 0 && <tr><td className="muted">Payments received</td><td className="tnum" style={{ textAlign: "right", color: "var(--green)", fontWeight: 600 }}>−{window.VFP.money(paid)}</td></tr>}
                          {paid > 0 && (() => {
                            const entries = [
                              ...(Array.isArray(latestQuote.stripePayments) && latestQuote.stripePayments.length ? latestQuote.stripePayments : (Number(latestQuote.depositPaid) > 0 ? [{ amount: Number(latestQuote.depositPaid), method: "stripe", at: null }] : [])),
                              ...(Array.isArray(latestQuote.payments) ? latestQuote.payments : []),
                            ];
                            const lab = (p: any) => p.method === "stripe" ? "Online payment" : p.method === "check" ? "Check" : p.method === "cash" ? "Cash" : "Payment";
                            return entries.map((p: any, i: number) => (
                              <tr key={"hp" + i}><td className="muted" style={{ fontSize: 11.5, paddingLeft: 12 }}>{lab(p)}{p.at ? " · " + new Date(p.at).toLocaleDateString() : ""}</td><td className="tnum muted" style={{ textAlign: "right", fontSize: 11.5 }}>−{window.VFP.money(p.amount)}</td></tr>
                            ));
                          })()}
                          {paid > 0 && <tr><td style={{ fontWeight: 800, color: fullPaid ? "var(--green)" : "var(--red)" }}>Amount due</td><td className="tnum display" style={{ textAlign: "right", fontSize: 18, color: fullPaid ? "var(--green)" : "var(--red)" }}>{window.VFP.money(bal)}</td></tr>}
                        </tfoot>
                      </table>
                      {(() => {
                        const cf = (state.settings && state.settings.cardFee) || {};
                        if (!cf.enabled || fullPaid) return null;
                        const pct = Math.max(1, Math.min(3, Number(cf.pct) || 3));
                        return <p className="muted" style={{ fontSize: 11.5, margin: "8px 0 0" }}>A {pct}% fee applies to credit cards. Debit and prepaid cards are not charged the fee.{bal > 0 ? <> Amount due {window.VFP.money(bal)} — {window.VFP.money(bal * (1 + pct / 100))} by credit card.</> : null} Bank transfer (ACH) or check — no fee.</p>;
                      })()}
                    </>
                  )}

                  {sig && sig.at && (
                    <p className="muted" style={{ fontSize: 12, margin: "14px 0 0" }}>
                      ✓ E-signed by <b style={{ color: "var(--fg-2)" }}>{sig.name}</b> on {new Date(sig.at).toLocaleString()} · agreement ref {String(sig.docHash || "").slice(0, 10)}
                    </p>
                  )}
                  {legalForm && legalForm !== coName && <p className="muted" style={{ fontSize: 11.5, margin: "6px 0 0" }}>Agreement with {legalForm}</p>}

                  {/* printed copy carries the QR back to this portal */}
                  {(() => {
                    const QR: any = (window as any).QrCode, UTM: any = (window as any).utmUrl;
                    if (!QR || !UTM) return null;
                    return (
                      <div className="print-only" style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
                        <QR url={UTM(window.location.origin + "/hub/" + token, "hub-invoice-print", "hub")} size={70} label="YOUR PROJECT PORTAL" sub="pay · schedule · track install" />
                      </div>
                    );
                  })()}

                  {isFence && latestQuote.inputs && (latestQuote.inputs.parcel || (latestQuote.inputs.mVerts || []).length > 1) && (
                    <div className="siteplan-block" style={{ marginTop: 18 }}>
                      <div className="row between" style={{ marginBottom: 6 }}><span className="kicker">Addendum — site plan</span><span className="faint" style={{ fontSize: 11 }}>Not to scale · lengths field-verified</span></div>
                      <SitePlan inputs={latestQuote.inputs} />
                    </div>
                  )}

                  {paid > 0 && bal > 0 && (
                    <div className="no-print" style={{ marginTop: 16 }}>
                      <HubPayChoice settings={state.settings} amount={bal} label="Pay balance securely" onPay={(m: any) => payFinal(latestQuote, bal, m)} />
                    </div>
                  )}
                  <p className="faint no-print" style={{ fontSize: 11, textAlign: "center", marginTop: 10 }}>Secured by Stripe · cards &amp; ACH</p>
                  {poweredBy && <PoweredBy medium="quote" />}
                </div>
              </div>
            </div>
          </div>
          </Portal>
        );
      })()}
    </div>
  );
}

function hubFmtTime(at: any) {
  const d = new Date(at); if (isNaN(d.getTime())) return "";
  const now = new Date(); const sameDay = d.toDateString() === now.toDateString();
  const t = d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
  return sameDay ? t : d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + " · " + t;
}

/* Pay buttons honouring the contractor's card-fee policy: fee off → the one
   button, exactly as before; fee on → bank transfer (no fee) leads and card
   shows the base AND the total with the fee before anyone taps — disclosed,
   avoidable. The fee is only ever charged on a credit card: the card is
   authorized with it, then captured without it when Stripe reports debit or
   prepaid (server/src/cardcapture.js) — so that is what this says. */
function HubPayChoice({ settings, amount, label, onPay }: any) {
  const cf = (settings && settings.cardFee) || {};
  const money0 = window.VFP.money0;
  if (!cf.enabled) {
    return (
      <button className="btn btn-primary btn-block" onClick={() => onPay(null)}>
        <Icon name="dollar" size={15} />{label} · {money0(amount)}
      </button>
    );
  }
  const pct = Math.max(1, Math.min(3, Number(cf.pct) || 3));
  const withFee = amount * (1 + pct / 100);
  return (
    <div style={{ display: "grid", gap: 8 }}>
      <button className="btn btn-primary btn-block" onClick={() => onPay("ach")}>
        <Icon name="dollar" size={15} />{label} by bank transfer · {money0(amount)} — no fee
      </button>
      <button className="btn btn-outline btn-block" onClick={() => onPay("card")}>
        {label} by card · {money0(amount)}&nbsp;<span className="muted" style={{ fontSize: 11.5 }}>{money0(withFee)} with the {pct}% credit-card fee</span>
      </button>
      <p className="muted" style={{ fontSize: 11.5, margin: 0 }}>
        A {pct}% fee applies to credit cards. Debit and prepaid cards are not charged the fee. The card checkout shows {money0(withFee)}; a debit or prepaid card is charged {money0(amount)} and the rest of the hold is released.
      </p>
    </div>
  );
}

/* Pick-your-install-window — shown in the schedule card whenever the
   contractor has offered windows (wo.offeredWindows, capacity-checked on
   their side) and no date is locked yet. The pick posts to
   /public/hub/:token/schedule-request (stamps wo.scheduleRequest for the
   contractor's one-tap confirm) with a message-thread fallback for an older
   deployed API; a local marker keeps the "requested" state across reloads
   until the confirmed date lands. */
function HubPickWindow({ wo, token, apiBase, company, phone }: any) {
  const LKEY = "fw_hub_pick_" + token;
  const [picked, setPicked] = React.useState<any>(() => {
    if (wo.scheduleRequest && wo.scheduleRequest.start) return wo.scheduleRequest.start;
    try { const v = JSON.parse(localStorage.getItem(LKEY) || "null"); return v && v.woId === wo.id ? v.start : null; } catch (e) { return null; }
  });
  const [err, setErr] = React.useState<any>("");
  // never offer a week that's already begun (stale stamp from a while back)
  const wins = (Array.isArray(wo.offeredWindows) ? wo.offeredWindows : [])
    .filter((w: any) => w && w.start && new Date(w.start + "T00:00:00").getTime() > Date.now());
  const fmtW = (s: any) => s ? new Date(s + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" }) : "";

  async function pick(w: any) {
    setErr("");
    setPicked(w.start);
    try { localStorage.setItem(LKEY, JSON.stringify({ woId: wo.id, start: w.start })); } catch (e) {}
    if (!apiBase) return;
    try {
      const r = await fetch(`${apiBase}/public/hub/${encodeURIComponent(token)}/schedule-request`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workOrderId: wo.id, start: w.start, end: w.end }),
      });
      if (!r.ok && r.status === 404) throw new Error("404");
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.message || d.error || "Request failed"); }
    } catch (e: any) {
      // older API → at least land the request in the contractor's messages.
      // Only stay optimistic once THAT actually lands; otherwise un-pick and
      // tell them how to reach us — a "Requested" card over a lost request is
      // a customer who never gets scheduled.
      let landed = false;
      try {
        const f = await fetch(`${apiBase}/public/hub/${encodeURIComponent(token)}/message`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: `📅 Install request — I'd like the week of ${fmtW(w.start)}.` }),
        });
        landed = f.ok;
      } catch (e2) { landed = false; }
      if (landed) { setErr(""); return; }   // fallback delivered — stay optimistic
      setPicked(null);
      try { localStorage.removeItem(LKEY); } catch (e3) {}
      setErr("Couldn't send that request — " + (phone ? "call us at " + phone : "please call " + company) + ".");
    }
  }

  if (picked) {
    return (
      <div className="row gap-10" style={{ alignItems: "center" }}>
        <div className="hub-date"><Icon name="check" size={18} /></div>
        <div>
          <div style={{ fontWeight: 700 }}>Requested — week of {fmtW(picked)}</div>
          <div className="muted" style={{ fontSize: 12.5 }}>{company} will confirm and the exact date will show here.</div>
        </div>
      </div>
    );
  }
  if (!wins.length) return <p className="muted" style={{ fontSize: 13 }}>We'll post your install date here once it's booked.</p>;
  return (
    <div>
      <p className="muted" style={{ fontSize: 13, margin: "0 0 10px" }}><b style={{ color: "var(--fg-2)" }}>Pick your install week</b> — these are open on {company}'s schedule right now:</p>
      <div style={{ display: "grid", gap: 8 }}>
        {wins.map((w: any, i: number) => (
          <button key={i} className="btn btn-outline btn-block" onClick={() => pick(w)}>
            <Icon name="calendar" size={14} />Week of {fmtW(w.start)}{w.end && w.end !== w.start ? ` · ~${Math.round((new Date(w.end + "T00:00:00").getTime() - new Date(w.start + "T00:00:00").getTime()) / 86400000) + 1} day install` : ""}
          </button>
        ))}
      </div>
      {err && <p style={{ color: "var(--amber)", fontSize: 12, margin: "8px 0 0" }}>{err}</p>}
      <p className="faint" style={{ fontSize: 11, margin: "8px 0 0" }}>Tap one and it's requested — {company} confirms the exact day.</p>
    </div>
  );
}

/* Change-order approval — the customer's side of a post-signature scope
   change. Pending COs (recorded on the quote by the contractor) list here with
   the price effect spelled out; one typed name + drawn signature approves them
   through /payments/hub/:token/approve-co (per-CO ESIGN audit trail, server
   timestamp). The revised total only takes effect once this happens. Falls
   back to posting the signed approval into the message thread if the deployed
   API predates the endpoint, so an approval is never silently dropped. */
function HubCOApproval({ q, current: shownTotal, token, apiBase, hubPost, company }: any) {
  const cos = Array.isArray(q.changeOrders) ? q.changeOrders : [];
  const pend = cos.filter((c: any) => c && c.status === "pending");
  const [name, setName] = React.useState<any>("");
  const [agree, setAgree] = React.useState<any>(false);
  const [err, setErr] = React.useState<any>("");
  const [done, setDone] = React.useState<any>(null);   // "approved" | "sent"
  if (!pend.length || done === "reload") return null;
  const money = window.VFP.money0;
  const pendSum = pend.reduce((s: number, c: any) => s + (Number(c.amount) || 0), 0);
  // the total the rest of the page shows (hubQuoteView: base + approved COs).
  // Re-deriving it here from q.total counted the approved COs twice on an
  // unsigned quote — q.total already carries them.
  const approvedSum = cos.filter((c: any) => c && (!c.status || c.status === "approved")).reduce((s: number, c: any) => s + (Number(c.amount) || 0), 0);
  const current = shownTotal != null && isFinite(Number(shownTotal)) ? Number(shownTotal)
    : Math.max(0, q.signedTotal != null ? Number(q.signedTotal) + approvedSum : Number(q.total) || 0);
  const after = Math.max(0, current + pendSum);

  async function approveAll(image: any) {
    setErr("");
    if (!agree) { setErr("Please check the agreement box first."); return; }
    if (!name || name.trim().length < 2) { setErr("Please type your full legal name first."); return; }
    try {
      for (const c of pend) {
        await hubPost("approve-co", { quoteId: q.id, coId: c.id, name: name.trim(), image, agree: true });
      }
      setDone("approved");
      setTimeout(() => { try { window.location.reload(); } catch (e) {} }, 1600);
    } catch (e: any) {
      const msg = String((e && e.message) || e);
      // older deployed API → deliver the signed approval through the message
      // thread instead so it still reaches the contractor with the signature
      if (/\(404\)/.test(msg) && apiBase) {
        try {
          const f = await fetch(`${apiBase}/public/hub/${encodeURIComponent(token)}/message`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              body: `✍️ CHANGE-ORDER APPROVAL — I, ${name.trim()}, approve: ${pend.map((c: any) => `${c.label} (${(Number(c.amount) || 0) >= 0 ? "+" : "−"}${money(Math.abs(Number(c.amount) || 0))})`).join("; ")}. New total ${money(after)}.`,
              image,
            }),
          });
          if (!f.ok) throw new Error("message " + f.status);
          setDone("sent");
          return;
        } catch (e2) { /* fall through to the error line */ }
      }
      setErr(msg);
    }
  }

  return (
    <div className="hub-card" style={{ borderColor: done ? "var(--green)" : "var(--amber)" }}>
      <div className="kicker" style={{ marginBottom: 12 }}>{done ? "Change approved" : "Approve your change order" + (pend.length > 1 ? "s" : "")}</div>
      {done ? (
        <div className="row gap-8" style={{ alignItems: "center" }}>
          <Icon name="check" size={16} style={{ color: "var(--green)" }} />
          <span style={{ fontSize: 13.5 }}>
            {done === "approved"
              ? <>Thank you — your approval is recorded. Your project total is now <b>{money(after)}</b>.</>
              : <>Thank you — your signed approval went to {company}; they'll confirm it and your total will update here.</>}
          </span>
        </div>
      ) : (
        <div style={{ display: "grid", gap: 12 }}>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>{company} recorded {pend.length > 1 ? "changes" : "a change"} to your project. Nothing is charged until you approve it here.</p>
          <div style={{ border: "1px solid var(--line)", borderRadius: 10, padding: "10px 14px" }}>
            {pend.map((c: any) => (
              <div key={c.id} className="row between" style={{ fontSize: 13, padding: "4px 0", gap: 10 }}>
                {c.photo && <a className="co-photo" href={c.photo} target="_blank" rel="noopener" style={{ backgroundImage: `url(${c.photo})` }} title="See the photo" />}
                <span style={{ minWidth: 0, flex: 1 }}>{c.label}{c.at ? <span className="muted" style={{ fontSize: 11.5 }}> · {new Date(c.at).toLocaleDateString()}</span> : null}</span>
                <b className="tnum" style={{ color: (Number(c.amount) || 0) >= 0 ? "var(--fg)" : "var(--green)", flexShrink: 0 }}>{(Number(c.amount) || 0) >= 0 ? "+" : "−"}{money(Math.abs(Number(c.amount) || 0))}</b>
              </div>
            ))}
            <div className="row between" style={{ fontSize: 12.5, marginTop: 6, paddingTop: 6, borderTop: "1px solid var(--line)" }}>
              <span className="muted">Current total</span><span className="tnum">{money(current)}</span>
            </div>
            <div className="row between" style={{ fontSize: 14, fontWeight: 800 }}>
              <span>New total after approval</span><span className="tnum" style={{ color: "var(--teal)" }}>{money(after)}</span>
            </div>
          </div>
          <div>
            <label className="field-label">Type your full legal name</label>
            <input className="input" value={name} onChange={e => setName(e.target.value)} placeholder="Your full name" autoComplete="name" />
          </div>
          <SignaturePad signed={false} onSign={approveAll} />
          <label className="row gap-8" style={{ fontSize: 12, color: "var(--fg-3)", alignItems: "flex-start", cursor: "pointer" }}>
            <input type="checkbox" checked={agree} onChange={e => setAgree(e.target.checked)} style={{ marginTop: 2 }} />
            <span>I have reviewed {pend.length > 1 ? "these change orders" : "this change order"} and its effect on my project total, and my signature is a legally binding approval (E-SIGN Act / UETA).</span>
          </label>
          {err && <p style={{ color: "var(--red)", fontSize: 12.5, margin: 0 }}>{err}</p>}
          <p className="faint" style={{ fontSize: 11, margin: 0 }}>Questions first? Use "Message us" below — nothing changes until you sign.</p>
        </div>
      )}
    </div>
  );
}

// a tier's button spans its card and wraps on a phone, where three cards share ~330px
const TIER_BTN: any = { marginTop: "auto", width: "100%", justifyContent: "center", whiteSpace: "normal", lineHeight: 1.25, fontSize: 11.5, padding: "5px 6px" };
/* good/better/best tiers on the hub: same fence re-priced per product; the
   customer's pick posts into the contractor's Messages via the hub messenger
   endpoint, and the contractor applies it from the quote in one click. */
function HubOptions({ q, state, token, apiBase }: any) {
  const [sent, setSent] = React.useState<any>(null);
  const [sendFail, setSendFail] = React.useState<any>(null);   // the tier whose request didn't land (tap to retry)
  const products = [...((state && state.customProducts) || []), ...window.VFP.PRODUCTS];
  // option totals come from the stamped customer document (no pricing inputs
  // on the public hub); the engine is only a fallback where they exist locally
  const docTotals = (q.customerDoc && q.customerDoc.optionTotals) || {};
  const priceFor = (pid: any) => {
    const p = products.find((x: any) => x.id === pid);
    if (!p) return null;
    if (pid === q.productId) { try { return { p, total: hubQuoteView(q, state).quote.customerTotal }; } catch (e) { return null; } }
    if (docTotals[pid] != null) return { p, total: Number(docTotals[pid]) };
    if (!q.inputs || !(state && state.settings && state.settings.markup != null)) return null;
    try { return { p, total: window.VFP.computeQuote({ ...q.inputs, productId: pid }, p, (state && state.settings) || {}).customerTotal }; } catch (e) { return null; }
  };
  // the contractor's pick wears a Recommended badge (quote.tsx recommendedTierId)
  const recId = recommendedTierId(q);
  const tiers = quoteTiers(q)
    .map((t: any) => ({ ...t, priced: priceFor(t.productId) })).filter((t: any) => t.priced);
  if (tiers.length < 2) return null;
  async function choose(t: any) {
    setSent(t.label); setSendFail(null);
    if (apiBase && token) {
      let ok = false;
      try {
        const r = await fetch(`${apiBase}/public/hub/${encodeURIComponent(token)}/message`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ body: `I'd like the ${t.label} option — ${t.priced.p.name} (${window.VFP.money0(t.priced.total)})` }),
        });
        ok = r.ok;
      } catch (e) { ok = false; }
      if (!ok) { setSent(null); setSendFail(t); }   // never show "Request sent" over a lost request
    }
  }
  return (
    <div style={{ margin: "4px 0 12px" }}>
      <div className="kicker" style={{ marginBottom: recId ? 14 : 8 }}>Your options — same fence, {tiers.length === 2 ? "two" : "three"} ways</div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(3, tiers.length)}, minmax(0, 1fr))`, gap: 8 }}>
        {tiers.map((t: any) => (
          <div key={t.id} style={{ position: "relative", border: recId === t.id ? "2px solid var(--green)" : "1px solid " + (t.current ? "var(--teal-tint-2)" : "var(--line)"), borderRadius: 12, padding: recId === t.id ? "15px 8px 9px" : "10px 9px", textAlign: "center", display: "flex", flexDirection: "column", minWidth: 0 }}>
            {recId === t.id && <span className="chip green" style={{ position: "absolute", top: -10, left: "50%", transform: "translateX(-50%)", fontSize: 9.5, whiteSpace: "nowrap", background: "var(--surface)" }}><Icon name="check" size={10} />Recommended</span>}
            <div className="muted" style={{ fontSize: 10, fontWeight: 800, textTransform: "uppercase", letterSpacing: ".06em" }}>{t.label}</div>
            <div style={{ fontWeight: 700, fontSize: 12.5, margin: "3px 0" }}>{t.priced.p.name}</div>
            <div className="display tnum" style={{ fontSize: 18, color: "var(--teal)", marginBottom: 6 }}>{window.VFP.money0(t.priced.total)}</div>
            {t.current
              ? <div className="faint" style={{ fontSize: 10.5, marginTop: "auto" }}>currently quoted</div>
              : sent === t.label
                ? <div style={{ fontSize: 11, marginTop: "auto", color: "var(--green)", fontWeight: 700 }}>✓ Request sent</div>
                : sendFail && sendFail.id === t.id
                  ? <button className="btn btn-outline btn-sm" style={{ ...TIER_BTN, color: "var(--amber)" }} onClick={() => choose(t)}>Not delivered — tap to retry</button>
                  : <button className="btn btn-outline btn-sm" style={TIER_BTN} onClick={() => choose(t)}>I want this one</button>}
          </div>
        ))}
      </div>
      {sent && <p className="faint" style={{ fontSize: 11, margin: "8px 0 0", textAlign: "center" }}>Got it — we'll update your quote to the {sent} option and you'll see the new total here.</p>}
    </div>
  );
}

/* the customer-facing blueprint: same PlanSheet(s) the contractor prints,
   rendered inline from the hub's workspace snapshot (read-only) */
function HubFencePlan({ q, state }: any) {
  const products = [...((state && state.customProducts) || []), ...window.VFP.PRODUCTS];
  let view: any = null;
  try { view = hubQuoteView(q, state); } catch (e) { return null; }
  const sections = view.fenceQuotes
    ? view.fenceQuotes.map((f: any) => ({ name: f.name, prod: f.prod, calc: f.q, inputs: f.snap }))
    : [{ name: null, prod: view.product, calc: view.quote, inputs: view.inputs }];
  const drawable = sections.filter((s: any) => { const poly = (s.inputs && s.inputs.parcel && s.inputs.parcel.polygon) || (s.inputs && s.inputs.mVerts) || []; return poly.length >= 2; });
  if (!drawable.length) return null;
  const shim = { company: state.company, settings: state.settings, projects: state.projects || [] };
  return (
    <div style={{ marginTop: 12 }}>
      {drawable.map((s: any, i: number) => (
        <PlanSheet key={i} inputs={s.inputs} prod={s.prod} calc={s.calc} name={s.name} sheetNo={i + 1} sheetCount={drawable.length} q={q} store={shim} />
      ))}
    </div>
  );
}

/* ---- "Send us photos": up to six at a time, downscaled in the browser, to
   POST /public/hub/:token/photos — the server puts them on the job when there
   is one, else the newest open concrete estimate (and reads them again), and
   always on the customer's card and the thread. ---- */
function HubSendPhotos({ token, isFence, wo, latestQuote, sandboxLocal, apiBase }: any) {
  const [picked, setPicked] = React.useState<any[]>([]);      // { file, thumb }
  const [note, setNote] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const [sent, setSent] = React.useState<any>(null);          // { count, attachedTo, urls }
  const [err, setErr] = React.useState<any>("");
  const fileRef = React.useRef<any>(null);
  const hasJob = !!(wo && !["completed", "canceled"].includes(String(wo.status || "")));   // any open job is where the photos go (the server picks the same one)
  const openEstimate = !!(latestQuote && !["signed", "paid", "lost"].includes(String(latestQuote.status || "")));
  async function pick(files: FileList | null) {
    const list = Array.from(files || []).slice(0, 6 - picked.length);
    if (!list.length) return;
    const next: any[] = [];
    for (const f of list) { try { next.push({ file: f, thumb: await downscaleToDataUrl(f, 320, 0.6) }); } catch (e) { /* not an image */ } }
    setPicked((p: any[]) => [...p, ...next].slice(0, 6)); setSent(null); setErr("");
  }
  async function send() {
    if (!picked.length || busy) return;
    setBusy(true); setErr("");
    try {
      const photos: string[] = [];
      for (const p of picked) photos.push(await downscaleToDataUrl(p.file, 1600, 0.8));
      const body = { photos, note: note.trim() || undefined, workOrderId: wo ? wo.id : undefined, quoteId: latestQuote ? latestQuote.id : undefined };
      let d: any;
      if (sandboxLocal) d = demoHubAction(token, "photos", body);
      else {
        if (!apiBase) throw new Error("Sending photos needs the online service.");
        const r = await fetch(`${apiBase}/public/hub/${encodeURIComponent(token)}/photos`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
        d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.message || d.error || ("Request failed (" + r.status + ")"));
      }
      setSent({ count: (d.urls || photos).length, attachedTo: d.attachedTo || (hasJob ? "job" : openEstimate ? "quote" : "customer"), urls: d.urls || picked.map((p: any) => p.thumb) });
      setPicked([]); setNote("");
    } catch (e: any) { setErr(String((e && e.message) || e)); }
    setBusy(false);
  }
  const where = hasJob ? "They go straight onto your job, where the crew sees them." : openEstimate ? (isFence ? "They go onto your estimate." : "They go straight onto your estimate — we read them for the drop, the cracks and the floor's condition.") : "They land with your account; we'll see them right away.";
  const ask = isFence ? "The property lines, the gates, anything in the way — a few photos save a trip." : "The slab from a few steps back, the worst edge, where it meets the house — a few photos save a trip.";
  return (
    <div className="hub-card" data-hub-photos>
      <div className="kicker" style={{ marginBottom: 6 }}>Send us photos</div>
      <p className="muted" style={{ fontSize: 13, margin: "0 0 10px" }}>{ask} {where}</p>
      <input ref={fileRef} type="file" accept="image/*" multiple capture="environment" style={{ display: "none" }} onChange={e => { pick(e.target.files); e.currentTarget.value = ""; }} />
      {picked.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))", gap: 8, marginBottom: 10 }}>
          {picked.map((p: any, i: number) => (
            <span key={i} style={{ position: "relative", aspectRatio: "1 / 1", borderRadius: 10, overflow: "hidden", display: "block", backgroundImage: `url(${p.thumb})`, backgroundSize: "cover", backgroundPosition: "center" }}>
              <button type="button" aria-label="Remove" onClick={() => setPicked((x: any[]) => x.filter((_, k) => k !== i))} style={{ position: "absolute", top: 3, right: 3, border: 0, borderRadius: 99, width: 22, height: 22, background: "rgba(0,0,0,.55)", color: "#fff", cursor: "pointer", fontSize: 13 }}>×</button>
            </span>
          ))}
        </div>
      )}
      {picked.length > 0 && <input className="hub-composer-input" style={{ width: "100%", marginBottom: 10, boxSizing: "border-box" }} placeholder="A note about them (optional)" value={note} onChange={e => setNote(e.target.value)} />}
      <div className="row gap-8" style={{ flexWrap: "wrap", alignItems: "center" }}>
        <button className="btn btn-outline btn-sm" type="button" disabled={busy || picked.length >= 6} onClick={() => fileRef.current && fileRef.current.click()}><Icon name="plus" size={13} />{picked.length ? "Add more" : "Choose photos"}</button>
        {picked.length > 0 && <button className="btn btn-primary btn-sm" type="button" disabled={busy} onClick={send}><Icon name="send" size={13} />{busy ? "Sending…" : `Send ${picked.length} photo${picked.length === 1 ? "" : "s"}`}</button>}
        <span className="faint" style={{ fontSize: 11.5 }}>Up to 6 at a time.</span>
      </div>
      {err && <p style={{ color: "var(--red)", fontSize: 12.5, margin: "8px 0 0" }}>{err}</p>}
      {sent && (
        <div style={{ marginTop: 10 }}>
          <p style={{ fontSize: 13, margin: "0 0 8px", color: "var(--green)" }}>✓ Sent {sent.count} photo{sent.count === 1 ? "" : "s"} — {sent.attachedTo === "job" ? "they're on your job." : sent.attachedTo === "quote" ? "they're on your estimate." : "we have them."}</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(72px, 1fr))", gap: 8 }}>
            {sent.urls.map((u: string, i: number) => <a key={i} href={u} target="_blank" rel="noopener" style={{ aspectRatio: "1 / 1", borderRadius: 10, display: "block", backgroundImage: `url(${u})`, backgroundSize: "cover", backgroundPosition: "center" }} />)}
          </div>
        </div>
      )}
    </div>
  );
}

function HubMessenger({ company, thing, token, initial, sandboxLocal, post }: any) {
  // server messages → bubble shape ("me" = the customer viewing the hub)
  const fromThread = (arr: any[]) => (arr || []).map((m: any) => ({ from: m.role === "customer" ? "me" : "company", text: m.body, image: m.image, at: m.at || m.created_at }));
  // sandbox: the same thread the contractor sees on the Messages page
  const fromLocal = (arr: any[]) => (arr || []).map((m: any) => ({ from: m.from === "customer" ? "me" : "company", text: m.text, image: m.image, at: m.at }));
  const greeting = { from: "company", text: `Hi! Reach out here anytime with questions about your ${thing || "fence"} project.`, at: null };
  const [msgs, setMsgs] = React.useState<any>(() => [greeting, ...(sandboxLocal ? fromLocal(demoHubThread(token)) : fromThread(initial))]);
  const [text, setText] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const apiBase = (window.VFP_API_BASE || "").replace(/\/$/, "");
  const fileRef = React.useRef<any>(null);
  const scrollRef = React.useRef<any>(null);
  // keep the thread pinned to the most recent message (load + new messages)
  React.useEffect(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs.length]);

  // sandbox: poll the shared sandbox state so the contractor's replies appear
  // here, and anything sent here shows up on their Messages page
  React.useEffect(() => {
    if (!sandboxLocal || !token) return;
    const tick = () => setMsgs([greeting, ...fromLocal(demoHubThread(token))]);
    const iv = setInterval(tick, 1500);
    return () => clearInterval(iv);
  }, [sandboxLocal, token]);

  // poll the server thread so the contractor's replies appear without a reload
  React.useEffect(() => {
    if (sandboxLocal || !apiBase || !token) return;
    let dead = false;
    const tick = () => fetch(`${apiBase}/public/hub/${encodeURIComponent(token)}?probe=1`)   // a thread poll, never "the customer opened it"
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (!dead && d && d.state && Array.isArray(d.state.hubThread)) setMsgs((m: any[]) => [greeting, ...fromThread(d.state.hubThread), ...m.filter((x: any) => x.localId)]); })
      .catch(() => {});
    const iv = setInterval(tick, 8000);
    return () => { dead = true; clearInterval(iv); };
  }, [apiBase, token]);

  // POST one bubble's payload; the bubble is tagged by localId until it lands
  // (or is marked failed with a tap-to-retry — never a silently lost message)
  async function deliver(localId: string, body: string, image?: string) {
    let ok = false;
    try {
      const r = await fetch(`${apiBase}/public/hub/${encodeURIComponent(token)}/message`, {
        method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ body, image }),
      });
      ok = r.ok;
    } catch (e) { ok = false; }
    setMsgs((m: any[]) => m.map((x: any) => x.localId === localId ? (ok ? { ...x, localId: undefined, failed: false } : { ...x, failed: true }) : x));
  }
  function retry(m: any) {
    if (!m.localId) return;
    setMsgs((cur: any[]) => cur.map((x: any) => x.localId === m.localId ? { ...x, failed: false } : x));
    deliver(m.localId, m.text, m.image);
  }
  async function send(image?: string) {
    const body = text.trim();
    if (!body && !image) return;
    setText("");
    if (sandboxLocal && post) {
      // lands in the sandbox store → the contractor's Messages page sees it
      setMsgs((m: any[]) => [...m, { from: "me", text: body, image, at: Date.now() }]);   // optimistic; the poll reconciles
      try { await post("message", { body, image }); } catch (e) {}
      setMsgs([greeting, ...fromLocal(demoHubThread(token))]);
      return;
    }
    if (apiBase && token) {
      const localId = "loc-" + Date.now() + "-" + Math.floor(Math.random() * 1e4);
      setMsgs((m: any[]) => [...m, { from: "me", text: body, image, at: new Date().toISOString(), localId }]);   // optimistic
      // persisted: lands in the contractor's conversations/messages tables
      deliver(localId, body, image);
    } else {
      setMsgs((m: any[]) => [...m, { from: "me", text: body, image, at: new Date().toISOString() }]);
      setTimeout(() => setMsgs((m: any[]) => [...m, { from: "company", text: "Thanks — we'll get right back to you!", at: new Date().toISOString() }]), 700);
    }
  }
  async function attachPhoto(file: File) {
    if (!file) return;
    setBusy(true);
    try { send(await downscaleToDataUrl(file)); } catch (e) {} finally { setBusy(false); }
  }
  return (
    <div>
      <div className="hub-msgs" ref={scrollRef}>
        {msgs.map((m: any, i: number) => (
          <div key={i} style={{ alignSelf: m.from === "me" ? "flex-end" : "flex-start", maxWidth: "82%" }}>
            <div className={"hub-msg " + m.from}>
              {m.image && <a href={m.image} target="_blank" rel="noopener"><img src={m.image} alt="" style={{ maxWidth: 220, borderRadius: 8, display: "block", marginBottom: m.text ? 6 : 0 }} /></a>}
              {m.text}
            </div>
            {m.failed
              ? <button className="btn btn-ghost btn-sm" style={{ fontSize: 10.5, margin: "2px 0 0", padding: "2px 6px", color: "var(--amber)", float: "right" }} onClick={() => retry(m)}>Not delivered — tap to retry</button>
              : m.at && <div className="faint" style={{ fontSize: 10.5, margin: "2px 4px 0", textAlign: m.from === "me" ? "right" : "left" }}>{hubFmtTime(m.at)}</div>}
          </div>
        ))}
      </div>
      {/* one pill: attach on the left, the field in the middle, send on the
          right — the field used to be width:100% in a wrapping row, so it took
          a line of its own and pushed both buttons onto their own rows */}
      <div className="hub-composer">
        <input ref={fileRef} type="file" accept="image/*" style={{ display: "none" }} onChange={e => { const f = e.target.files && e.target.files[0]; if (f) attachPhoto(f); e.currentTarget.value = ""; }} />
        <button className="hub-attach" type="button" title="Attach a photo" aria-label="Attach a photo" disabled={busy} onClick={() => fileRef.current && fileRef.current.click()}>
          <Icon name="plus" size={17} />
        </button>
        <input className="hub-composer-input" placeholder="Type a message…" value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter") send(); }} />
        <button className="hub-send" type="button" aria-label="Send" disabled={busy || !text.trim()} onClick={() => send()}><Icon name="send" size={15} /></button>
      </div>
    </div>
  );
}

/* ---- public router: decides hub vs widget vs app from the hash ---- */
/* ============================================================
   PublicChat — the embeddable site chat-bubble panel (rendered inside the
   contractor's website via the loader snippet's iframe). Talks to the AI web
   concierge through /public/widget/:token/chat. An optional phone field lets a
   visitor opt into a text-back (which also persists the thread for the
   contractor); without it the bot still answers anonymously.
   ============================================================ */
function PublicChat({ token, state }: any) {
  const cleanToken = String(token || "").split("?")[0];
  const apiBase = (window.VFP_API_BASE || "").replace(/\/$/, "");
  const co = (state && state.company) || {};
  const company = co.name || "Fence Works";
  const coPhone = String(co.phone || (state && state.settings && state.settings.companyPhone) || "").trim();
  const accent = (state && state.settings && state.settings.widget && state.settings.widget.accent) || "#19e3c2";
  const greeting = `Hi! 👋 Questions about your fence project? Ask away — or leave your number and ${company} will text you right back.`;
  // an honest reply when the service can't be reached — never a fake "we'll be right with you"
  const unreachable = `Sorry — I can't reach our system right now. ${coPhone ? "Call " + coPhone : "Call " + company} or leave your number and we'll text you back.`;

  const [msgs, setMsgs] = React.useState<any>([{ role: "agent", content: greeting }]);
  const [input, setInput] = React.useState<any>("");
  const [phone, setPhone] = React.useState<any>("");
  const [busy, setBusy] = React.useState<any>(false);
  const scroller = React.useRef<any>(null);
  React.useEffect(() => { const el = scroller.current; if (el) el.scrollTop = el.scrollHeight; }, [msgs, busy]);
  // Esc inside the chat iframe asks the host-page snippet to hide the panel
  // (keystrokes land in whichever document has focus, so both sides listen).
  React.useEffect(() => {
    const onKey = (e: any) => { if (e.key === "Escape") { try { if (window.parent && window.parent !== window) window.parent.postMessage("fw-widget-close", "*"); } catch (err) {} } };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  async function send() {
    const text = input.trim();
    if (!text || busy) return;
    const history = msgs.filter((m) => m.role === "agent" || m.role === "user").map((m) => ({ role: m.role, content: m.content }));
    const next = msgs.concat([{ role: "user", content: text }]);
    setMsgs(next); setInput(""); setBusy(true);
    try {
      if (!apiBase) {   // offline / in-app preview with no service configured
        setMsgs(next.concat([{ role: "agent", content: "Thanks! We'll be right with you." }]));
      } else {
        const r = await fetch(`${apiBase}/public/widget/${encodeURIComponent(cleanToken)}/chat`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: text, history, phone: phone.trim() || null }),
        });
        if (!r.ok) throw new Error("chat " + r.status);
        const d = await r.json();
        setMsgs(next.concat([{ role: "agent", content: (d && d.reply) || unreachable }]));
      }
    } catch (e) {
      setMsgs(next.concat([{ role: "agent", content: unreachable }]));
    } finally { setBusy(false); }
  }

  return (
    <div className="pubchat" style={{ ["--waccent" as any]: accent, display: "flex", flexDirection: "column", height: "100vh", background: "var(--bg, #0d1117)" }}>
      <div className="pubchat-head" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", borderBottom: "1px solid var(--line, #232a33)", background: accent + "14" }}>
        <CompanyLogo company={co} size={30} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 800, fontSize: 14.5 }}>{company}</div>
          <div className="muted" style={{ fontSize: 11 }}><span style={{ color: accent }}>●</span> Typically replies in a few minutes</div>
        </div>
      </div>

      <div ref={scroller} className="pubchat-body" style={{ flex: 1, overflowY: "auto", padding: 14, display: "flex", flexDirection: "column", gap: 8 }}>
        {msgs.map((m, i) => (
          <div key={i} style={{ alignSelf: m.role === "user" ? "flex-end" : "flex-start", maxWidth: "82%",
            background: m.role === "user" ? accent : "var(--card-2, #161b22)",
            color: m.role === "user" ? "#04201b" : "var(--fg, #e6edf3)",
            padding: "9px 12px", borderRadius: 14, fontSize: 13.5, lineHeight: 1.4, whiteSpace: "pre-wrap",
            borderBottomRightRadius: m.role === "user" ? 4 : 14, borderBottomLeftRadius: m.role === "user" ? 14 : 4 }}>
            {m.content}
          </div>
        ))}
        {busy && <div style={{ alignSelf: "flex-start", color: "var(--fg-2, #9aa6b2)", fontSize: 12, padding: "2px 4px" }}>typing…</div>}
      </div>

      <div className="pubchat-foot" style={{ borderTop: "1px solid var(--line, #232a33)", padding: 10, display: "grid", gap: 8 }}>
        <input className="input" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Your phone for a text-back (optional)" inputMode="tel"
          style={{ fontSize: 12.5, padding: "8px 10px" }} />
        <div style={{ display: "flex", gap: 8 }}>
          <input className="input" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") send(); }}
            placeholder="Type your message…" style={{ flex: 1 }} autoFocus />
          <button className="btn" onClick={send} disabled={busy || !input.trim()} style={{ background: accent, color: "#04201b", fontWeight: 700, border: 0, padding: "0 16px" }}>
            <Icon name="send" size={15} />
          </button>
        </div>
        <div className="faint" style={{ fontSize: 10, textAlign: "center" }}>Powered by {company}</div>
      </div>
    </div>
  );
}

/* ============================================================
   PublicShare3D — /build3d/<token>: a clean, public, branded page with JUST
   the customer's fence in the 3D construction viewer. Same data source
   (/public/hub/<token> → one customer's scoped state) and same quote→viewer
   mapping (quote3DProps) as the hub, so the fence here is identical to the
   hub's "Watch your fence come together".
   ============================================================ */
function PublicShare3D({ token: rawToken, state }: any) {
  const token = String(rawToken || "").split("?")[0];
  const customers = (state && state.customers) || [];
  const customer = resolveHubCustomer(state, token);
  const company = (state && state.settings && state.settings.companyName) || "Fence Works";
  const co = (state && state.company) || {};
  const phone = String(co.phone || (state && state.settings && state.settings.companyPhone) || "").trim();

  if (!customer) {
    return (
      <div className="hub-shell"><div className="hub-card" style={{ textAlign: "center", maxWidth: 440, margin: "80px auto" }}>
        <LogoMark size={36} />
        <h2 className="display" style={{ fontSize: 20, margin: "14px 0 6px" }}>Link not found</h2>
        <p className="muted" style={{ fontSize: 13.5 }}>This 3D fence link is invalid or has been revoked. Please ask {company} for an updated link.</p>
      </div></div>
    );
  }

  const quotes = (state.quotes || []).filter(q => q.customerId === customer.id || (q.customer || "").toLowerCase() === (customer.name || "").toLowerCase());
  const latestQuote = quotes[quotes.length - 1];
  const first = (customer.name || "there").split(" ")[0];

  return (
    <div className="hub-shell">
      <div className="hub-topbar">
        <div className="row gap-10" style={{ alignItems: "center" }}><CompanyLogo company={co} size={26} /><b style={{ fontSize: 16 }}>{company}</b></div>
        <a className="btn btn-ghost btn-sm" style={{ flexShrink: 0 }} href={"/hub/" + encodeURIComponent(token)}>View your full project →</a>
      </div>

      <div className="hub-main" style={{ maxWidth: 880 }}>
        <div className="hub-hero" style={{ textAlign: "center" }}>
          <span className="kicker kicker-teal">Your fence · interactive 3D</span>
          <h1 className="display" style={{ fontSize: 26, margin: "6px 0 4px" }}>{first}, here&rsquo;s your fence in 3D</h1>
          <p className="muted" style={{ fontSize: 14, margin: 0 }}>Watch it go together piece by piece — drag to spin, pinch to zoom, ⛶ for fullscreen.</p>
        </div>

        {latestQuote ? (
          <div className="hub-card">
            <React.Suspense fallback={<div className="muted" style={{ textAlign: "center", padding: "72px 0", fontSize: 13 }}>Loading your fence in 3D…</div>}>
              <FenceBuild3DLazy {...quote3DProps(latestQuote, state)} compact />
            </React.Suspense>
          </div>
        ) : (
          <div className="hub-card" style={{ textAlign: "center" }}>
            <p className="muted" style={{ fontSize: 13.5, margin: 0 }}>Your 3D fence preview will appear here once {company} drafts your estimate.</p>
          </div>
        )}

        <div style={{ textAlign: "center" }}>
          <a className="btn btn-outline btn-sm" href={"/hub/" + encodeURIComponent(token)}>View your full project →</a>
        </div>
        <p className="faint" style={{ fontSize: 11.5, textAlign: "center", margin: "2px 0 20px" }}>
          Built with Fence Works{phone ? <> · Questions? Call {company} at <a href={"tel:" + phone} style={{ color: "var(--teal)", fontWeight: 600 }}>{phone}</a></> : <> · prepared for you by {company}</>}
        </p>
        {poweredByShown(state.poweredBy, !!(window.VFP_DEMO && window.VFP_DEMO.active) || !!state.sandbox, state.settings && state.settings.plan) && <PoweredBy medium="hub" />}
      </div>
    </div>
  );
}

function PublicRoute({ hash, state }: any) {
  const m = (hash || "").match(/^#\/(hub|widget|chat|build3d)\/(.+)$/);
  if (!m) return null;
  const [, kind, token] = m;
  if (kind === "hub") return <PublicCustomerHub token={token} state={state} />;
  if (kind === "build3d") return <PublicShare3D token={token} state={state} />;
  if (kind === "chat") return <PublicChat token={token} state={state} />;
  if (kind === "widget") {
    const w = (state && state.settings && state.settings.widget) || {};
    // query flags on the widget URL pick the form factor + opening tab:
    //   ?floating=1 → corner "Get a Quote" launcher (the floating embed renders
    //                 this inside its own iframe); otherwise the inline widget.
    //   ?tab=book   → open straight to the "Book a consult" form.
    const qs = new URLSearchParams(String(token).split("?")[1] || "");
    const cleanToken = String(token).split("?")[0];
    const floating = qs.get("floating") === "1" || qs.get("floating") === "true";
    const defaultTab = qs.get("tab") === "book" ? "book" : qs.get("tab") === "visual" ? "visual" : undefined;
    //   ?form=concrete → the concrete photo estimate form, under that service's
    //                    brand (the server puts state.concrete on the payload when
    //                    the workspace runs it; otherwise the fence estimator)
    if (qs.get("form") === "concrete" && state && state.concrete && state.concrete.enabled) {
      return (
        <div className="widget-standalone">
          <PublicConcreteForm state={state} widgetToken={cleanToken} store={null} />
        </div>
      );
    }
    if (floating) {
      return <FloatingWidget settings={state && state.settings} accent={w.accent || "#19e3c2"} headline={w.headline} store={null} widgetToken={cleanToken} defaultTab={defaultTab} standalone />;
    }
    return (
      <div className="widget-standalone">
        <PublicEstimatorForm settings={state && state.settings} accent={w.accent || "#19e3c2"} headline={w.headline} store={null} widgetToken={cleanToken} defaultTab={defaultTab} />
      </div>
    );
  }
  return null;
}

Object.assign(window, { PublicCustomerHub, PublicRoute, PublicChat, PublicShare3D, HubMessenger });

export { PublicCustomerHub, HubMessenger, PublicRoute, PublicChat, PublicShare3D };
// the in-person pad on the quote screen shows the same terms and sends the same
// version (the server refuses one it can't produce)
export { TERMS as CONTRACT_TERMS, TERMS_VERSION as CONTRACT_TERMS_VERSION, TERMS_CONCRETE as CONTRACT_TERMS_CONCRETE, TERMS_CONCRETE_VERSION as CONTRACT_TERMS_CONCRETE_VERSION };
